import React, { useState, useEffect, useRef } from 'react';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  Save, 
  ArrowBack, 
  Add, 
  Delete, 
  Business, 
  Person, 
  Home, 
  LocalShipping, 
  Build,
  Receipt,
  ExpandMore,
  Edit,
  CheckCircle,
  Cancel,
  LocalOffer
} from '@mui/icons-material';
import {
  BudgetCreate,
  BudgetVersionCreate,
  BudgetVersionFullSave,
  Product,
  InstallationService,
  TipoInstalacion,
  PriceList,
  FreightRate
} from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { setNavigationBlocked } from '../../utils/navigationGuard';
import {
  stagedInstallationHasData,
  normalizeStagedInstallation,
  computeEffectiveInstallationEnvironments,
  stagedFreightHasData,
  computeEffectiveFreights,
} from '../../utils/stagedRows';
import { removeInstallationByEnvRef } from '../../utils/environmentMutations';
import { applyAmbienteDiscount, clampLineDiscount, initialLineDiscount, isOutletLine } from '../../utils/materialDiscount';

// Constante para el ambiente de instalaciones generales (oculto en materiales)
const GENERAL_INSTALLATIONS_ENV_NAME = '__INSTALACIONES_GENERALES__';
const GENERAL_INSTALLATIONS_INDEX = -1;

interface EnvironmentFormData {
  nombre: string;
  descripcion: string;
  orden: number;
  descuento_materiales: number;  // % "Aplicar descuento" del ambiente (líneas no-outlet)
  descuentoTildado?: boolean;     // UI-only: checkbox tildado mientras el % está vacío
  materials: MaterialFormData[];
  installations: InstallationFormData[];
}

interface MaterialFormData {
  // Producto existente o nuevo
  product_id?: number;
  product?: Product;
  es_in_situ: boolean;
  
  // Datos del material
  descripcion: string;
  codigo: string;
  unidades: number;
  precio_unitario: number;
  currency: 'USD' | 'ARS';
  
  // Datos de cálculo
  metros_cuadrados?: number;
  cantidad_calculada?: number;
  cantidad_manual?: number;
  usar_cantidad_manual?: boolean;
  
  // Opciones de producto
  con_desperdicio?: boolean;
  con_estructura?: boolean;

  // Descuentos de la línea (CLAUDE.md §8.7 outlet model), fracciones.
  // `descuento_producto` lo captura el servidor (sólo outlet);
  // `descuento_linea` es el aplicado - ver utils/materialDiscount.
  descuento_producto?: number;
  descuento_linea?: number;

  // Observaciones
  observaciones?: string;
}

interface InstallationFormData {
  installation_service_id?: number;
  installation_service?: InstallationService;
  tipo_instalacion?: TipoInstalacion;
  cantidad_manual?: number; // B6: empieza vacío, no prellenado en 1
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom
  // Sin currency propio (CLAUDE.md §7, decidido 2026-08-30): Mano de Obra es
  // siempre USD, catálogo o 'custom' por igual.
}

interface FreightFormData {
  tipo_envio: string;
  descripcion_detalle?: string;
  monto: number;
  tipo: 'fijo' | 'porcentaje';
  currency: 'USD' | 'ARS';
  // Link back to the FreightRate catalog row this line was built from
  // (CLAUDE.md §8.5/§7 - real zone x capacity-tier lookup). `codigo` is
  // copied server-side from the referenced rate, not sent by the client -
  // kept here only so the UI can show it before the next reload. Both
  // undefined for a manual entry with no catalog rate behind it.
  freight_rate_id?: number;
  codigo?: string;
  freight_rate?: FreightRate;
}

interface BudgetFormData extends BudgetCreate {
  // Datos de la primera versión
  version_titulo: string;
  version_numero: string;
  vendedor: string;
  email_vendedor: string;
  tel_vendedor: string;
  condiciones_contratacion: string;
  condiciones_adicionales: string;
  descuento_instalacion: number;
  descuento_fletes: number;
  // Lista de precios asignada a esta versión (CLAUDE.md §4/§6, Phase 4/6 gap
  // cerrado 2026-09-01): sin esto, el picker de Materiales no tiene contra
  // qué lista gatear ni de dónde sacar el precio server-side.
  price_list_id?: number;

  // Datos de ambientes, fletes, etc.
  environments: EnvironmentFormData[];
  freights: FreightFormData[];
}

const BudgetFormNew: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtener el usuario autenticado

  // Tope de descuento del rep para la lista de precios elegida (CLAUDE.md
  // §4/§8.11, item H). Viene de las asignaciones del usuario autenticado
  // (max_discount_pct, en puntos porcentuales: 15 = 15%). null = sin tope
  // configurado o sin lista elegida -> no se advierte nada. Sólo una
  // advertencia visual: el backend permite el envío y lo desvía a
  // pending_approval si se excede (no se bloquea la carga acá).
  const getMaterialesDiscountCap = (priceListId?: number): number | null => {
    if (!priceListId || !user?.price_list_assignments) return null;
    const assignment = user.price_list_assignments.find(
      a => a.price_list_id === priceListId
    );
    const cap = assignment?.max_discount_pct;
    return cap === undefined || cap === null ? null : Number(cap);
  };
  
  // Estados para productos
  const [products, setProducts] = useState<Product[]>([]);

  // Listas de precios asignadas al rep (CLAUDE.md §4) y el mapa de precios
  // {product_id: price} de la lista elegida - null significa "sin lista
  // elegida todavía", que es el estado en el que el picker no gatea nada
  // (mismo skip que el backend aplica cuando la versión no tiene
  // price_list_id, para no bloquear a un rep sin lista asignada).
  const [myPriceLists, setMyPriceLists] = useState<PriceList[]>([]);
  const [priceMap, setPriceMap] = useState<Record<number, { price: number; descuento: number }> | null>(null);
  const [loadingPriceMap, setLoadingPriceMap] = useState(false);

  // IVA % del país activo (CLAUDE.md §6: "Materiales muestra US$ + IVA como
  // un solo número"; el % es config por país). Este form es un draft en
  // memoria todavía no persistido, así que no puede llamar a
  // get_version_section_summary (requiere una BudgetVersion ya guardada) -
  // se trae por separado con el mismo endpoint que ya usa Calculadora/
  // BudgetDetailNew, y se le aplica acá la misma fórmula exacta que usa
  // BudgetService.get_version_section_summary (materiales_usd * (1 +
  // iva_pct/100)) para no divergir de esa fuente única.
  const [ivaPercentage, setIvaPercentage] = useState<number>(0);
  
  // Estados principales
  const [formData, setFormData] = useState<BudgetFormData>({
    proyecto: '',
    cliente: '',
    responsable: '',
    email_cliente: '',
    tel_cliente: '',
    version_titulo: '', // Ahora empieza vacío, y si no se llena se utilizará el valor de "proyecto" 
    version_numero: '1.0', // Se mantiene por compatibilidad pero ya no se usa (lo genera el backend)
    vendedor: user?.name || '',
    email_vendedor: user?.email || '',
    tel_vendedor: user?.telefono || '',
    condiciones_contratacion: 'Presupuesto válido por 7 días\nPlazo de entrega: A convenir.\nValores expresados en dólares USD: cotización Dólar Oficial tipo vendedor Banco Nación. https://www.bna.com.ar/Personas\nLa factura se emite en pesos argentinos\nForma de pago: anticipado\n\nFormas de pago materiales:\n•⁠  ⁠Contado efectivo o Transferencia Bancaria\n•⁠  ⁠Tarjeta 6 cuotas: Pago del 32% adicional y 6 cuotas fijas en pesos\n•⁠  ⁠Tarjeta 12 cuotas: Pago del 70% adicional y 12 cuotas fijas en pesos\n•⁠  ⁠Tarjeta de credito del Banco Galicia: 3 cuotas sin interes pagando los miércoles o viernes',
    condiciones_adicionales: 'Presupuesto sujeto a modificaciones, luego de Visita Técnica a obra o al inicio de la misma.\nEn caso de que durante el desarrollo de la obra se precise más mercadería, el cliente deberá abonar los materiales restantes y asumir el costo de mano de obra adicional.\nLa colocación la realizan equipos terciarizados certificados y auditados por LifeCycle\nMetodología de pago de mano de obra: 50% previo al comienzo de los trabajos y 50% una vez finalizados los trabajos, previa conformidad del cliente.\nEl presupuesto de mano de obra se ajustará por IPC\nEl presupuesto de colocación no incluye andamios ni volquetes\nEl flete no esta incluido en el presupuesto, el mismo se coordinará una vez confirmada la fecha y lugar de entrega.',
    descuento_instalacion: 0,
    descuento_fletes: 0,
    environments: [],
    freights: []
  });

  // Guard de "cambios sin guardar" (Navbar/Cancelar/tab-close/refresh - ver
  // hooks/useUnsavedChangesGuard.ts). `initialFormDataRef` guarda el snapshot
  // "limpio" contra el que se compara `formData` en cada render: arranca con
  // el valor por defecto de arriba (el estado del formulario recién abierto,
  // antes de que cualquier carga async lo toque), y se resincroniza cada vez
  // que un cambio a `formData` es una carga de datos (loadConditions) y no
  // una edición real del usuario - así el formulario no aparece "sucio" apenas
  // se abre. isDirty se recalcula por render, no es un flag manual: evita que
  // alguna de las muchas rutas que mutan formData (diálogos de ambiente/
  // material/flete/instalación, descuentos, etc.) se olvide de marcarlo.
  const initialFormDataRef = useRef<string>(JSON.stringify(formData));
  const isDirty = JSON.stringify(formData) !== initialFormDataRef.current;
  useUnsavedChangesGuard(isDirty);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Item D: alerta de validación PROMINENTE al crear (antes el submit
  // bloqueado sólo dejaba errores de campo + un <Alert> al pie que parecía
  // "no pasó nada"). Un diálogo modal con el detalle - se usa tanto para la
  // validación cliente como para el rechazo del backend en el catch.
  const [submitErrorDialog, setSubmitErrorDialog] = useState<{ open: boolean; messages: string[] }>({
    open: false,
    messages: [],
  });
  
  // Estados para diálogos
  const [environmentDialog, setEnvironmentDialog] = useState(false);
  const [materialDialog, setMaterialDialog] = useState(false);
  const [freightDialog, setFreightDialog] = useState(false);
  const [installationDialog, setInstallationDialog] = useState(false);
  const [showCondiciones, setShowCondiciones] = useState(false);
  
  // Estados para formularios de diálogos
  const [selectedEnvironmentIndex, setSelectedEnvironmentIndex] = useState<number | null>(null);
  
  // Estados para installation services
  const [installationServices, setInstallationServices] = useState<InstallationService[]>([]);
  const [loadingInstallationServices, setLoadingInstallationServices] = useState(false);
  const [newEnvironment, setNewEnvironment] = useState<EnvironmentFormData>({
    nombre: '',
    descripcion: '',
    orden: 1, // Se mantendrá aunque no se muestre en el formulario
    descuento_materiales: 0,
    materials: [],
    installations: []
  });
  const [newMaterial, setNewMaterial] = useState<MaterialFormData>({
    descripcion: '',
    codigo: '',
    unidades: 0,
    precio_unitario: 0,
    currency: 'USD',
    es_in_situ: false,
    metros_cuadrados: undefined, // B6: empieza vacío, no prellenado en 1
    usar_cantidad_manual: false,
    con_desperdicio: true, // Preseleccionado por defecto
    con_estructura: false,
    observaciones: ''
  });
  const [newFreight, setNewFreight] = useState<FreightFormData>({
    tipo_envio: '',
    monto: 0,
    tipo: 'fijo',
    currency: 'ARS'
  });
  // Catálogo real de Fletes (CLAUDE.md §6/§7/§8.5: zona x tramo de
  // capacidad) - antes el diálogo de Flete no lo consultaba en absoluto.
  const [freightRates, setFreightRates] = useState<FreightRate[]>([]);
  const [loadingFreightRates, setLoadingFreightRates] = useState(false);
  const [freightMode, setFreightMode] = useState<'catalog' | 'manual'>('catalog');
  const [newInstallation, setNewInstallation] = useState<InstallationFormData>({});
  const [installationMode, setInstallationMode] = useState<'catalog' | 'custom'>('catalog');
  // B5: las filas de alta de MO/Flete quedan abiertas tras cada "Agregar", así
  // que autoFocus-on-mount no vuelve a disparar. Bumpeamos este contador en cada
  // alta y lo usamos como React key del picker para forzar un remount (y que su
  // autoFocus vuelva a correr). Materiales cierra su fila al agregar, así que
  // ahí alcanza con autoFocus.
  const [moPickerTick, setMoPickerTick] = useState(0);
  const [fletePickerTick, setFletePickerTick] = useState(0);
  const [alertStock, setAlertStock] = useState<string | null>(null);
  const [showAlertStock, setShowAlertStock] = useState(false);

  // Edición en línea de ítems ya cargados (Materiales/MO/Flete) - reabre la
  // misma fila inline de alta, precargada, en vez de un flujo separado
  // (Phase 8.7 Round A, item 2).
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<{ envIndex: number; matIndex: number } | null>(null);
  const [editingInstallationIndex, setEditingInstallationIndex] = useState<{ envIndex: number; instIndex: number } | null>(null);
  const [editingFreightIndex, setEditingFreightIndex] = useState<number | null>(null);
  
  // Estado para edición de nombres de ambientes (solo local, no persiste hasta guardar)
  const [editingEnvironmentIndex, setEditingEnvironmentIndex] = useState<number | null>(null);
  const [editingEnvironmentName, setEditingEnvironmentName] = useState<string>('');

  // Cargar productos al inicio
  useEffect(() => {
    loadProducts();
    loadInstallationServices();
    loadFreightRates();
    loadConditions();
    loadMyPriceLists();
    loadCountryConfig();
  }, []);

  // IVA % (y flete_enabled, sin uso acá) del país activo - CLAUDE.md §6.
  const loadCountryConfig = async () => {
    try {
      const config = await BudgetApiService.getCountryConfig();
      setIvaPercentage(Number(config.iva_percentage) || 0);
    } catch (error) {
      console.error('Error loading country config:', error);
      // Si falla, se mantiene 0% - el fallback server-side de
      // get_iva_percentage no ayuda acá porque este cálculo es 100%
      // client-side (draft todavía no persistido); mostrar "sin IVA" en vez
      // de adivinar un % es más seguro que un valor hardcodeado.
    }
  };

  // Catálogo real de Fletes (zona x tramo de capacidad) para el picker de la
  // sección Flete - CLAUDE.md §6/§7: "Flete → structural lookup (zone x
  // capacity tier) + manual entries". Solo tarifas activas, mismo criterio
  // de scoping que loadInstallationServices usa para su propio catálogo.
  const loadFreightRates = async () => {
    try {
      setLoadingFreightRates(true);
      const rates = await apiService.freightRates.getAll(true);
      setFreightRates(Array.isArray(rates) ? rates : []);
    } catch (error) {
      console.error('Error loading freight rates:', error);
      setError('Error al cargar tarifas de flete');
    } finally {
      setLoadingFreightRates(false);
    }
  };

  // Listas de precios asignadas al rep, en el país activo (CLAUDE.md §4:
  // "un rep solo puede elegir entre las listas que le fueron asignadas").
  const loadMyPriceLists = async () => {
    try {
      const lists = await apiService.priceLists.getMine();
      setMyPriceLists(lists);

      // Al crear un presupuesto nuevo, preseleccionar "General Público" si el
      // rep tiene esa lista asignada - es la lista por defecto/fallback
      // (CLAUDE.md §4). No forzar nada si el rep no la tiene asignada: se
      // deja sin elegir, igual que antes (el rep elige a mano entre lo que
      // sí tiene). BudgetFormNew es exclusivamente el formulario de
      // creación (no hay modo edición acá - eso es BudgetDetailNew/
      // BudgetEditNew), así que no hace falta distinguir "nuevo vs
      // editando": siempre es "nuevo".
      const generalPublico = lists.find(pl => pl.name === 'General Público');
      if (generalPublico) {
        setFormData(prev => (
          prev.price_list_id ? prev : { ...prev, price_list_id: generalPublico.id }
        ));
      }
    } catch (error) {
      console.error('Error loading price lists:', error);
    }
  };

  // Traer el mapa {product_id: price} de la lista elegida cada vez que
  // cambia - es lo que el picker de Materiales usa para gatear/priciar
  // (ver handleProductSelect). Sin lista elegida, el mapa queda en null y
  // el picker no gatea nada (mismo skip que el backend).
  useEffect(() => {
    const priceListId = formData.price_list_id;
    if (!priceListId) {
      setPriceMap(null);
      return;
    }
    let cancelled = false;
    setLoadingPriceMap(true);
    apiService.priceLists.getPrices(priceListId)
      .then(map => {
        if (!cancelled) setPriceMap(map);
      })
      .catch(error => {
        console.error('Error loading price list prices:', error);
        if (!cancelled) setPriceMap(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPriceMap(false);
      });
    return () => { cancelled = true; };
  }, [formData.price_list_id]);

  // CLAUDE.md §4: "falta precio para un producto en la lista elegida por el
  // rep -> bloquear. El producto aparece grisado con un mensaje". Sin lista
  // elegida (priceMap === null) no se gatea nada.
  const isProductGated = (product: Product): boolean => {
    return priceMap !== null && !(product.id in priceMap);
  };

  // Cargar condiciones desde la API
  const loadConditions = async () => {
    try {
      const response = await apiService.get<{ condiciones_generales_default: string; condiciones_adicionales_default: string }>('/app-settings/conditions');
      setFormData(prev => {
        const updated = {
          ...prev,
          condiciones_contratacion: response.condiciones_generales_default || prev.condiciones_contratacion,
          condiciones_adicionales: response.condiciones_adicionales_default || prev.condiciones_adicionales
        };
        // Esto es una carga de datos, no una edición del usuario - resincronizar
        // el snapshot "limpio" del guard de cambios sin guardar acá evita que el
        // formulario aparezca "sucio" apenas se abre (ver initialFormDataRef).
        initialFormDataRef.current = JSON.stringify(updated);
        return updated;
      });
    } catch (error) {
      console.error('Error loading conditions from app settings:', error);
      // Si falla, se mantienen los valores por defecto
    }
  };

  // Cargar installation services
  const loadInstallationServices = async () => {
    try {
      setLoadingInstallationServices(true);
      // Crear token header manualmente como hace el resto de la app
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
  const response = await fetch(`/api/installation-services?is_active=true&limit=100`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const services: InstallationService[] = await response.json();
      setInstallationServices(Array.isArray(services) ? services : []);
    } catch (error) {
      console.error('Error loading installation services:', error);
      setError('Error al cargar servicios de instalación');
    } finally {
      setLoadingInstallationServices(false);
    }
  };

  // Actualizar datos del vendedor cuando cambia el usuario
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        vendedor: user.name || '',
        email_vendedor: user.email || '',
        tel_vendedor: user.telefono || ''
      }));
    }
  }, [user]);

  const loadProducts = async () => {
    try {
      const productsData = await apiService.products.getAll({ active_only: true, limit: 1000 });
      setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.proyecto.trim()) {
      newErrors.proyecto = 'El nombre del proyecto es requerido';
    }
    
    if (!formData.cliente.trim()) {
      newErrors.cliente = 'El nombre del cliente es requerido';
    }
    
    // Título de versión ya no es requerido porque se puede usar proyecto como valor por defecto
    
    if (!formData.vendedor.trim()) {
      newErrors.vendedor = 'El nombre del vendedor es requerido';
    }
    
    if (formData.email_cliente && !/\S+@\S+\.\S+/.test(formData.email_cliente)) {
      newErrors.email_cliente = 'El email no tiene un formato válido';
    }
    
    if (formData.email_vendedor && !/\S+@\S+\.\S+/.test(formData.email_vendedor)) {
      newErrors.email_vendedor = 'El email no tiene un formato válido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en los campos
  const handleChange = (field: keyof BudgetFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Funciones para manejar ambientes
  const handleAddEnvironment = () => {
    if (!newEnvironment.nombre.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      environments: [...prev.environments, { ...newEnvironment, orden: prev.environments.length + 1 }]
    }));
    
    setNewEnvironment({
      nombre: '',
      descripcion: '',
      orden: 1, // Mantenemos el valor por defecto aunque no se muestre en el formulario
      descuento_materiales: 0,
      materials: [],
      installations: []
    });
    setEnvironmentDialog(false);
  };

  const handleDeleteEnvironment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.filter((_, i) => i !== index)
    }));
  };

  // Funciones de edición de nombre de ambiente (solo actualiza estado local)
  const handleStartEditEnvironmentName = (envIndex: number, currentName: string) => {
    setEditingEnvironmentIndex(envIndex);
    setEditingEnvironmentName(currentName);
  };

  const handleCancelEditEnvironmentName = () => {
    setEditingEnvironmentIndex(null);
    setEditingEnvironmentName('');
  };

  const handleSaveEnvironmentName = (envIndex: number) => {
    // Validar nombre no vacío
    const trimmedName = editingEnvironmentName.trim();
    if (!trimmedName) {
      setError('El nombre del ambiente no puede estar vacío');
      return;
    }

    // Actualizar estado local
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.map((env, idx) =>
        idx === envIndex ? { ...env, nombre: trimmedName } : env
      )
    }));

    // Limpiar estado de edición
    setEditingEnvironmentIndex(null);
    setEditingEnvironmentName('');
  };

  // Funciones para manejar materiales
  const handleAddMaterial = () => {
    // Bloqueo real (no solo visual, ver el botón "Agregar Material" y su
    // disabled más abajo): sin una lista de precios elegida no hay contra
    // qué verificar el precio de Materiales, así que no se puede completar
    // el alta - antes esto caía en un fallback silencioso a "precio de
    // catálogo sin verificar", que ya no existe (CLAUDE.md §4).
    if (!formData.price_list_id) return;
    if (selectedEnvironmentIndex === null) return;
    
    // Validar según si es producto existente o in situ
    if (!newMaterial.es_in_situ) {
      if (!newMaterial.product_id) return;
      // B6: no agregar sin una cantidad resuelta (m² o cantidad manual > 0).
      const qty = newMaterial.usar_cantidad_manual ? newMaterial.cantidad_manual : newMaterial.cantidad_calculada;
      if (!qty || qty <= 0) return;
    } else {
      if (!newMaterial.codigo.trim() || !newMaterial.descripcion.trim()) return;
    }

    // Verificar stock antes de agregar
    if (newMaterial.product && typeof newMaterial.product.stock === 'number') {
      const cantidadPresupuestada = newMaterial.unidades;
      if (cantidadPresupuestada > newMaterial.product.stock) {
        setAlertStock(
          `Estás por presupuestar más de lo que hay en stock (${newMaterial.product.stock}). Revisa los próximos ingresos.`
        );
        // No hacer return, solo mostrar la alerta
      } else {
        setAlertStock(null);
      }
    }
    
    // Calcular cantidad final
    let cantidadFinal = newMaterial.unidades;
    if (!newMaterial.es_in_situ && newMaterial.product && newMaterial.metros_cuadrados) {
      if (newMaterial.usar_cantidad_manual && newMaterial.cantidad_manual) {
        cantidadFinal = newMaterial.cantidad_manual;
      } else if (newMaterial.cantidad_calculada) {
        cantidadFinal = newMaterial.cantidad_calculada;
      }
    }


    let materialToAdd: MaterialFormData = {
      ...newMaterial,
      unidades: cantidadFinal,
      currency: newMaterial.currency || 'USD'
    };

    // Si el material es de grupo ESTRUCTURA, forzar flags y observaciones igual que los auto-agregados
    if (newMaterial.product && newMaterial.product.grupo === 'ESTRUCTURA') {
      materialToAdd = {
        ...materialToAdd,
        metros_cuadrados: undefined,
        con_estructura: false,
        con_desperdicio: false,
        usar_cantidad_manual: false,
        observaciones: 'Estructura para ', // código vacío
      };
    }

    // Preparar lista de materiales a agregar (incluye el material principal)
    const materialsToAdd: MaterialFormData[] = [materialToAdd];

    // Si tiene estructura habilitada, procesar y agregar materiales de estructura
    if (newMaterial.con_estructura && newMaterial.product?.estructura_m2) {
      const cantidadBase = (newMaterial.product.grupo === 'PERFIL')
        ? (newMaterial.unidades || 1)
        : (newMaterial.metros_cuadrados || 1);
      const structureMaterials = processStructureMaterials(
        newMaterial.product.estructura_m2,
        cantidadBase,
        newMaterial.con_desperdicio || false,
        newMaterial.product.codigo
      );
      materialsToAdd.push(...structureMaterials);
    }

    // Agregar todos los materiales al ambiente
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      const targetEnv = newEnvironments[selectedEnvironmentIndex];
      // Descuento inicial de la línea (CLAUDE.md §8.7): outlet -> el de
      // producto; no-outlet -> el % del ambiente (0 si no está tildado).
      targetEnv.materials.push(...materialsToAdd.map(m => ({
        ...m, descuento_linea: initialLineDiscount(m, targetEnv.descuento_materiales),
      })));
      return { ...prev, environments: newEnvironments };
    });

    setNewMaterial({
      descripcion: '',
      codigo: '',
      unidades: 0,
      precio_unitario: 0,
      currency: 'USD',
      es_in_situ: false,
      metros_cuadrados: undefined, // B6: empieza vacío
      usar_cantidad_manual: false,
      con_desperdicio: true,
      con_estructura: false,
      observaciones: ''
    });
    setMaterialDialog(false);
    setSelectedEnvironmentIndex(null);
  };

  // Función para calcular cantidad automáticamente basada en m².
  //
  // Reemplazado 2026-09-04 (Product Owner): antes usaba largo × ancho_util
  // del producto para derivar un área por unidad y dividir los m² por eso -
  // esa lógica queda retirada por completo, no como fallback. Ahora usa
  // directamente `factor_conversion_m2` del catálogo (CLAUDE.md §7,
  // CONVERSIONM2 en la planilla real) - un factor de **unidades por m²**,
  // no de m² por unidad: `cantidad = m² × factor`, no `m² ÷ área`.
  // `factor_conversion_m2` está 100% completo en el catálogo real (a
  // diferencia de `largo`/`ancho`, que eran mucho más dispersos - `ancho`
  // solo 44% completo), así que esto también corrige un problema real: la
  // lógica anterior devolvía "1 unidad" para la mayoría de los productos
  // por falta de dimensiones.
  const calculateMaterialQuantity = (product: Product, metros_cuadrados: number, con_desperdicio: boolean = false): number => {
    if (!product.factor_conversion_m2) return 1;

    // Calcular cuántas unidades necesito para cubrir los metros cuadrados
    let cantidad = Math.ceil(metros_cuadrados * product.factor_conversion_m2);

    // Aplicar desperdicio (10% adicional)
    if (con_desperdicio) {
      cantidad = Math.ceil(cantidad * 1.1);
    }

    return cantidad;
  };

  // Función para calcular m² basado en cantidad de unidades - inversa de
  // calculateMaterialQuantity, mismo `factor_conversion_m2` (unidades/m²).
  const calculateMetrosCuadradosFromQuantity = (product: Product, cantidad: number): number => {
    if (!product.factor_conversion_m2 || cantidad <= 0) return 0;

    return cantidad / product.factor_conversion_m2;
  };

  // Función para procesar materiales de estructura
  const processStructureMaterials = (estructura_m2: string, metros_cuadrados: number, con_desperdicio: boolean, productoPrincipalCodigo: string): MaterialFormData[] => {
    if (!estructura_m2?.trim()) return [];
    
    const structureMaterials: MaterialFormData[] = [];
    
    // Parsear estructura_m2 (formato: "codigo1,cantidad_por_m2;codigo2,cantidad_por_m2")
    const entries = estructura_m2.split(/[;\n]+/);
    
    for (const entry of entries) {
      const trimmedEntry = entry.trim();
      if (!trimmedEntry) continue;
      
      try {
        const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
        const codigoTrimmed = codigo.trim();
        const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
        
        // Buscar el producto por código
        const structureProduct = products.find((p: Product) => p.codigo === codigoTrimmed);
        if (!structureProduct) {
          console.warn(`Producto de estructura no encontrado: ${codigoTrimmed}`);
          continue;
        }
        
        // Calcular cantidad total: cantidad_por_m2 * metros_cuadrados
        let cantidad = cantidadPorM2 * metros_cuadrados;
        
        // NOTA: El desperdicio NO se aplica a los materiales de estructura
        // Solo se redondea hacia arriba para obtener un número entero de unidades
        cantidad = Math.ceil(cantidad);
        
        structureMaterials.push({
          descripcion: structureProduct.codigo,
          codigo: structureProduct.codigo,
          unidades: cantidad,
          // CLAUDE.md §4: Product ya no tiene precio propio - el precio real
          // lo resuelve el backend server-side desde la lista de precios al
          // guardar (BudgetService._resolve_materiales_precio_unitario).
          precio_unitario: (priceMap !== null ? priceMap[structureProduct.id]?.price : undefined) ?? 0,
          currency: 'USD',
          es_in_situ: false,
          product_id: structureProduct.id,
          product: structureProduct,
          metros_cuadrados: undefined, // ✅ Items de estructura NO tienen m2
          usar_cantidad_manual: false,
          con_desperdicio: false, // Los materiales de estructura NO tienen desperdicio
          con_estructura: false, // Los materiales de estructura no tienen estructura adicional
          observaciones: `Estructura para ${productoPrincipalCodigo}`
        });
      } catch (error) {
        console.warn('Error procesando entrada de estructura:', entry, error);
        continue;
      }
    }
    
    return structureMaterials;
  };

  // Recalcula las filas de estructura de la línea `matIndex` para que sigan la
  // cantidad actual del padre (round 4/5, item 4). Root-cause único: reusa
  // `processStructureMaterials` (misma fórmula que el alta y el toggle), quita
  // las filas de estructura viejas de este padre y reinserta las nuevas justo
  // debajo. Se llama desde todo punto que muta la cantidad del padre: caja de
  // cantidad inline, guardado del diálogo de edición, y el toggle. Los padres
  // no-PERFIL derivan la base de metros_cuadrados, así que quien cambia la
  // cantidad debe mantener m2 en sincronía primero (misma inversa que el alta
  // manual). Devuelve un array nuevo; no muta el recibido.
  const rebuildStructureRows = (materials: MaterialFormData[], matIndex: number): MaterialFormData[] => {
    const parent = materials[matIndex];
    const parentCodigo = parent?.product?.codigo || parent?.codigo;
    if (!parent?.con_estructura || !parent.product?.estructura_m2 || !parentCodigo) {
      return materials;
    }
    const cantidadBase = (parent.product.grupo === 'PERFIL')
      ? (parent.unidades || 1)
      : (parent.metros_cuadrados || 1);
    const fresh = processStructureMaterials(
      parent.product.estructura_m2,
      cantidadBase,
      parent.con_desperdicio || false,
      parentCodigo
    );
    const result = materials.filter(m => m.observaciones !== `Estructura para ${parentCodigo}`);
    const parentIdx = result.indexOf(parent);
    result.splice(parentIdx + 1, 0, ...fresh);
    return result;
  };

  // Mantiene metros_cuadrados coherente con una cantidad editada a mano, para
  // que la base de la cascada de estructura refleje el cambio en productos
  // no-PERFIL. Misma inversa que handleCantidadManualChange. Devuelve el
  // material padre actualizado (copia).
  const syncM2ToUnidades = (parent: MaterialFormData, nuevasUnidades: number): MaterialFormData => {
    const updated = { ...parent, unidades: nuevasUnidades };
    if (parent.product && !parent.es_in_situ && parent.product.grupo !== 'PERFIL' && parent.metros_cuadrados != null) {
      const base = parent.con_desperdicio ? nuevasUnidades / 1.1 : nuevasUnidades;
      const m2 = calculateMetrosCuadradosFromQuantity(parent.product, base);
      if (m2 > 0) updated.metros_cuadrados = Math.round(m2 * 100) / 100;
    }
    return updated;
  };

  // Función para manejar selección de producto
  const handleProductSelect = (product: Product | null) => {
    if (!product) {
      setNewMaterial(prev => ({
        ...prev,
        product_id: undefined,
        product: undefined,
        descripcion: '',
        codigo: '',
        precio_unitario: 0,
        cantidad_calculada: 0
      }));
      return;
    }

    // Defensa además del getOptionDisabled del Autocomplete (CLAUDE.md §4:
    // "no fallback a otra lista, bloquear sin excepción") - un producto sin
    // precio en la lista elegida no debe poder seleccionarse ni por acá.
    if (isProductGated(product)) {
      return;
    }

    // B6: sin m² todavía, no calculamos cantidad (nada de default a 1); el rep
    // debe tipear m² o cantidad antes de poder agregar.
    const cantidadCalculada = newMaterial.metros_cuadrados
      ? calculateMaterialQuantity(product, newMaterial.metros_cuadrados, newMaterial.con_desperdicio || false)
      : undefined;

    // Precio: si hay una lista elegida, el precio SIEMPRE sale del mapa de
    // esa lista - Product ya no tiene un precio de catálogo propio al que
    // recurrir (CLAUDE.md §4). El backend vuelve a exigir/resolver esto de
    // forma independiente (defense in depth, incluyendo el fallback a
    // "General Público" cuando la versión todavía no tiene lista elegida) -
    // esto es solo para que el resumen que ve el rep muestre un número
    // mientras tanto, no la fuente de verdad.
    const entry = priceMap !== null ? priceMap[product.id] : undefined;
    const precioUnitario = entry?.price ?? 0;
    // CLAUDE.md §8.7 outlet model: sólo un producto outlet trae descuento de
    // producto (fracción, de la entrada de la lista); no-outlet captura 0.
    const descuentoProducto = product.is_outlet ? (entry?.descuento ?? 0) : 0;

    setNewMaterial(prev => ({
      ...prev,
      product_id: product.id,
      product: product,
      descripcion: product.codigo,
      codigo: product.codigo,
      precio_unitario: precioUnitario,
      descuento_producto: descuentoProducto,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : (cantidadCalculada || 0)
    }));
  };

  // Función para manejar cambio en metros cuadrados
  const handleMetrosCuadradosChange = (metros: number | undefined) => {
    if (!newMaterial.product) return;

    // B6: sin m² no hay cantidad calculada (no default silencioso a 1).
    const cantidadCalculada = metros
      ? calculateMaterialQuantity(newMaterial.product, metros, newMaterial.con_desperdicio || false)
      : undefined;

    setNewMaterial(prev => ({
      ...prev,
      metros_cuadrados: metros,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : (cantidadCalculada || 0)
    }));
  };

  // Función para manejar cambio en cantidad manual
  const handleCantidadManualChange = (cantidad: number) => {
    if (!newMaterial.product) return;
    
    // Calcular metros cuadrados basado en la cantidad ingresada
    let cantidadBase = cantidad;
    
    // Si tiene desperdicio, necesitamos calcular la cantidad base (sin desperdicio)
    // para obtener los metros cuadrados reales que se quieren cubrir
    if (newMaterial.con_desperdicio) {
      cantidadBase = cantidad / 1.1; // Quitar el 10% de desperdicio
    }
    
    const metrosCalculados = calculateMetrosCuadradosFromQuantity(newMaterial.product, cantidadBase);
    
    setNewMaterial(prev => ({
      ...prev,
      cantidad_manual: cantidad,
      unidades: cantidad,
      metros_cuadrados: Math.round(metrosCalculados * 100) / 100 // Redondear a 2 decimales
    }));
  };

  // Función para manejar cambio en desperdicio
  const handleDesperdicioChange = (con_desperdicio: boolean) => {
    if (!newMaterial.product || !newMaterial.metros_cuadrados) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      newMaterial.metros_cuadrados, 
      con_desperdicio
    );
    
    setNewMaterial(prev => ({
      ...prev,
      con_desperdicio,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : (cantidadCalculada || 0)
    }));
  };

  const handleDeleteMaterial = (envIndex: number, matIndex: number) => {
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      newEnvironments[envIndex].materials = newEnvironments[envIndex].materials.filter((_, i) => i !== matIndex);
      return { ...prev, environments: newEnvironments };
    });
  };

  // Reabre una fila de material ya cargada para edición in-place (Phase 8.7
  // Round A, item 2) - misma fila/controles que el alta, precargada.
  const handleEditMaterial = (envIndex: number, matIndex: number) => {
    const material = formData.environments[envIndex].materials[matIndex];
    setNewMaterial({ ...material });
    setSelectedEnvironmentIndex(envIndex);
    setEditingMaterialIndex({ envIndex, matIndex });
    setMaterialDialog(true);
  };

  // Guarda los cambios de una fila de material existente. Si al editar se
  // ACTIVA/DESACTIVA "incluir estructura" (round 3, item 2), corre la misma
  // cascada que el alta: al activar genera y agrega las filas de estructura;
  // al desactivar quita las que había generado esta línea. Las filas de
  // estructura son sus propias filas independientes, tagueadas por
  // observaciones "Estructura para <codigo>".
  const handleUpdateMaterial = () => {
    if (!editingMaterialIndex) return;
    const { envIndex, matIndex } = editingMaterialIndex;
    const updatedMaterial: MaterialFormData = {
      ...newMaterial,
      currency: newMaterial.currency || 'USD'
    };
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      let materials = [...newEnvironments[envIndex].materials];
      materials[matIndex] = updatedMaterial;

      const parentCodigo = updatedMaterial.product?.codigo || updatedMaterial.codigo;
      // Estructura apagada -> quitar sus filas; encendida (recién o ya activa
      // con cantidad cambiada) -> recalcular las filas para seguir la cantidad
      // del padre (item 4). rebuildStructureRows cubre alta y recálculo.
      if (!updatedMaterial.con_estructura && parentCodigo) {
        materials = materials.filter(m => m.observaciones !== `Estructura para ${parentCodigo}`);
      } else if (updatedMaterial.con_estructura && updatedMaterial.product?.estructura_m2 && parentCodigo) {
        materials = rebuildStructureRows(materials, matIndex);
      }

      newEnvironments[envIndex] = { ...newEnvironments[envIndex], materials };
      return { ...prev, environments: newEnvironments };
    });
    setEditingMaterialIndex(null);
    setNewMaterial({
      descripcion: '',
      codigo: '',
      unidades: 0,
      precio_unitario: 0,
      currency: 'USD',
      es_in_situ: false,
      metros_cuadrados: undefined, // B6: empieza vacío
      usar_cantidad_manual: false,
      con_desperdicio: true,
      con_estructura: false,
      observaciones: ''
    });
    setMaterialDialog(false);
    setSelectedEnvironmentIndex(null);
  };

  // Funciones para manejar instalación
  const handleAddInstallation = () => {
    if (selectedEnvironmentIndex === null ||
        !newInstallation.cantidad_manual || newInstallation.cantidad_manual <= 0) {
      return;
    }

    // Validar según el modo
    if (installationMode === 'catalog' && !newInstallation.installation_service_id) {
      return;
    }
    if (installationMode === 'custom' && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2)) {
      return;
    }

    // Calcular subtotal - para unidad global no multiplicar por cantidad
    let precio = 0;
    if (installationMode === 'catalog') {
      precio = calculateInstallationPrice();
    } else {
      precio = newInstallation.mano_obra_m2 || 0;
    }
    const esGlobal = installationMode === 'custom' && newInstallation.unidad?.toLowerCase() === 'global';
    const subtotal = esGlobal ? precio : precio * (newInstallation.cantidad_manual || 0);

    // Determinar la unidad: para custom usar la seleccionada, para catálogo usar la del servicio
    let unidadFinal: string | undefined;
    if (installationMode === 'custom') {
      unidadFinal = newInstallation.unidad || '';
    } else if (newInstallation.installation_service) {
      unidadFinal = newInstallation.installation_service.unidad || '';
    }

    // Preparar la instalación con todos los campos necesarios.
    // installation_service_id ya identifica la variante exacta (CLAUDE.md
    // §7, decidido 2026-08-30) - tipo_instalacion solo distingue 'custom'.
    const installationToAdd = {
      ...newInstallation,
      subtotal,
      mano_obra_m2: precio,
      unidad: unidadFinal,
      tipo_instalacion: installationMode === 'custom' ? 'custom' as const : undefined
    };
    
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      
      // Si es el ambiente general (-1), buscarlo o crearlo
      if (selectedEnvironmentIndex === GENERAL_INSTALLATIONS_INDEX) {
        let generalEnvIndex = newEnvironments.findIndex(env => env.nombre === GENERAL_INSTALLATIONS_ENV_NAME);
        
        if (generalEnvIndex === -1) {
          // Crear el ambiente general
          newEnvironments.push({
            nombre: GENERAL_INSTALLATIONS_ENV_NAME,
            descripcion: 'Instalaciones generales del presupuesto',
            orden: 9999, // Al final
            descuento_materiales: 0,
            materials: [],
            installations: [installationToAdd]
          });
        } else {
          // Agregar al ambiente general existente
          newEnvironments[generalEnvIndex].installations = [
            ...newEnvironments[generalEnvIndex].installations,
            installationToAdd
          ];
        }
      } else {
        // Ambiente normal
        newEnvironments[selectedEnvironmentIndex].installations = [
          ...newEnvironments[selectedEnvironmentIndex].installations,
          installationToAdd
        ];
      }
      
      return { ...prev, environments: newEnvironments };
    });
    
    // Reset the entry form for the NEXT item - deliberately do NOT close the
    // dialog or clear the selected ambiente here: a rep should be able to
    // add several MO lines to one ambiente in one sitting (direct user ask,
    // 2026-09-04). The dialog now only closes via its own explicit close
    // affordance ("Listo" / backdrop / Escape below), never automatically
    // after a successful add.
    setNewInstallation({});
    setInstallationMode('catalog');
    setMoPickerTick(t => t + 1); // B5: refoco del picker en la nueva fila en blanco
  };

  // Reabre una instalación ya cargada para edición in-place (Phase 8.7
  // Round A, item 2).
  const handleEditInstallation = (envIndex: number, instIndex: number) => {
    const installation = formData.environments[envIndex].installations[instIndex];
    setNewInstallation({ ...installation });
    setInstallationMode(installation.installation_service_id ? 'catalog' : 'custom');
    setSelectedEnvironmentIndex(envIndex);
    setEditingInstallationIndex({ envIndex, instIndex });
    setInstallationDialog(true);
  };

  const handleUpdateInstallation = () => {
    if (!editingInstallationIndex) return;
    const { envIndex, instIndex } = editingInstallationIndex;

    let precio = 0;
    if (installationMode === 'catalog') {
      precio = calculateInstallationPrice();
    } else {
      precio = newInstallation.mano_obra_m2 || 0;
    }
    const esGlobal = installationMode === 'custom' && newInstallation.unidad?.toLowerCase() === 'global';
    const subtotal = esGlobal ? precio : precio * (newInstallation.cantidad_manual || 0);

    let unidadFinal: string | undefined;
    if (installationMode === 'custom') {
      unidadFinal = newInstallation.unidad || '';
    } else if (newInstallation.installation_service) {
      unidadFinal = newInstallation.installation_service.unidad || '';
    }

    const installationToUpdate = {
      ...newInstallation,
      subtotal,
      mano_obra_m2: precio,
      unidad: unidadFinal,
      tipo_instalacion: installationMode === 'custom' ? 'custom' as const : undefined
    };

    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      const installations = [...newEnvironments[envIndex].installations];
      installations[instIndex] = installationToUpdate;
      newEnvironments[envIndex] = { ...newEnvironments[envIndex], installations };
      return { ...prev, environments: newEnvironments };
    });

    setEditingInstallationIndex(null);
    setInstallationDialog(false);
    setSelectedEnvironmentIndex(null);
    setNewInstallation({});
    setInstallationMode('catalog');
  };

  // Manejar selección de installation service
  const handleInstallationServiceSelect = (service: InstallationService | null) => {
    if (!service) {
      setNewInstallation(prev => ({
        ...prev,
        installation_service_id: undefined,
        installation_service: undefined,
        tipo_instalacion: undefined,
        trabajo: undefined
      }));
      return;
    }

    // Calcular área del ambiente si hay uno seleccionado y la unidad es m²
    let cantidadInicial = 1;
    if (selectedEnvironmentIndex !== null && formData.environments[selectedEnvironmentIndex]) {
      const unidad = service.unidad?.toLowerCase() || '';
      if (unidad === 'm²' || unidad === 'm2' || unidad === 'm2') {
        const areaTotal = formData.environments[selectedEnvironmentIndex].materials.reduce(
          (total, mat) => total + (mat.metros_cuadrados || 0), 0
        );
        if (areaTotal > 0) {
          cantidadInicial = Math.round(areaTotal * 100) / 100; // Redondear a 2 decimales
        }
      }
    }

    setNewInstallation(prev => ({
      ...prev,
      installation_service_id: service.id,
      installation_service: service,
      // `trabajo` se retiró del catálogo (CLAUDE.md §7, decidido
      // 2026-09-03) - `descripcion` es ahora el único nombre.
      trabajo: service.descripcion || '',
      cantidad_manual: cantidadInicial
    }));
  };

  // Calcular precio (USD, siempre) del servicio seleccionado - cada
  // variante (Standard/Compleja) es su propia fila del catálogo con su
  // propio `precio` (CLAUDE.md §7, decidido 2026-08-30), así que no hay
  // rama por tipo de instalación acá.
  const calculateInstallationPrice = (): number => {
    // Si el usuario editó el precio (mano_obra_m2), usarlo directamente
    if (newInstallation.mano_obra_m2) {
      return newInstallation.mano_obra_m2;
    }

    return newInstallation.installation_service?.precio || 0;
  };

  // Funciones para manejar fletes
  // Selección de una tarifa real del catálogo FreightRate (CLAUDE.md
  // §6/§7/§8.5) - precarga tipo_envio/monto/currency desde la fila elegida,
  // igual que handleInstallationServiceSelect hace para Mano de Obra. El
  // monto queda editable después (Flete admite edición manual de precio,
  // CLAUDE.md §6).
  const handleFreightRateSelect = (rate: FreightRate | null) => {
    if (!rate) {
      setNewFreight(prev => ({
        ...prev,
        freight_rate_id: undefined,
        freight_rate: undefined,
        codigo: undefined
      }));
      return;
    }
    setNewFreight(prev => ({
      ...prev,
      freight_rate_id: rate.id,
      freight_rate: rate,
      codigo: rate.codigo,
      tipo_envio: rate.descripcion ? `${rate.zone} - ${rate.descripcion}` : rate.zone,
      monto: Number(rate.price) || 0,
      currency: (rate.currency as 'USD' | 'ARS') || 'ARS'
    }));
  };

  const handleAddFreight = () => {
    if (freightMode === 'catalog' && !newFreight.freight_rate_id) return;
    // B6: flete manual exige un monto > 0 (nunca alta silenciosa en 0)
    if (freightMode === 'manual' && (!newFreight.tipo_envio.trim() || !newFreight.monto || newFreight.monto <= 0)) return;

    setFormData(prev => ({
      ...prev,
      freights: [...prev.freights, { ...newFreight }]
    }));

    // Reset the entry form for the NEXT item - deliberately do NOT close the
    // dialog here, same "stay open until explicitly closed" fix as MO above.
    setNewFreight({
      tipo_envio: '',
      monto: 0,
      tipo: 'fijo',
      currency: 'ARS'
    });
    setFletePickerTick(t => t + 1); // B5: refoco del picker en la nueva fila en blanco
  };

  // Reabre un flete ya cargado para edición in-place (Phase 8.7 Round A,
  // item 2).
  const handleEditFreight = (index: number) => {
    const freight = formData.freights[index];
    setNewFreight({ ...freight });
    setFreightMode(freight.freight_rate_id ? 'catalog' : 'manual');
    setEditingFreightIndex(index);
    setFreightDialog(true);
  };

  const handleUpdateFreight = () => {
    if (editingFreightIndex === null) return;
    const index = editingFreightIndex;
    setFormData(prev => {
      const freights = [...prev.freights];
      freights[index] = { ...newFreight };
      return { ...prev, freights };
    });
    setEditingFreightIndex(null);
    setFreightDialog(false);
    setFreightMode('catalog');
    setNewFreight({
      tipo_envio: '',
      monto: 0,
      tipo: 'fijo',
      currency: 'ARS'
    });
  };

  const handleDeleteFreight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      freights: prev.freights.filter((_, i) => i !== index)
    }));
  };

  // Calcular totales separados por moneda
  const calculateTotals = () => {
    let materialesUSD = 0;
    let materialesARS = 0; // Mantenemos esta variable por compatibilidad pero no se mostrará
    let totalInstalacion = 0;
    // Mano de Obra separada por moneda (CLAUDE.md §8.5, restaurado
    // 2026-09-01): catálogo usa installation_service.currency, 'custom'
    // sigue siendo siempre USD. `totalInstalacion` se conserva como suma
    // cruda por compatibilidad, pero no debe mostrarse etiquetada con una
    // sola moneda - usar instalacionUSD/instalacionARS para eso.
    let instalacionUSD = 0;
    let instalacionARS = 0;

    formData.environments.forEach(env => {
      env.materials.forEach(mat => {
        const subtotal = mat.unidades * mat.precio_unitario;
        if (mat.currency === 'USD') {
          materialesUSD += subtotal;
        }
        // Nota: Ignoramos los materiales en ARS para los totales de materiales
      });

      if (env.installations && env.installations.length > 0) {
        // Sumar todas las instalaciones de este ambiente
        for (const installation of env.installations) {
          let subtotalInstalacion = 0;
          let currency = 'USD';

          if (installation.installation_service) {
            // Usar el precio editado (mano_obra_m2) si existe, sino usar el
            // del catálogo - cada variante ya es su propia fila con su
            // propio precio (CLAUDE.md §7, decidido 2026-08-30).
            const precio = installation.mano_obra_m2 || installation.installation_service.precio;

            if (precio) {
              subtotalInstalacion = precio * (installation.cantidad_manual ?? 1);
            }
            currency = installation.installation_service.currency || 'USD';
          } else if (installation.mano_obra_m2) {
            // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
            const esGlobal = installation.unidad?.toLowerCase() === 'global';
            subtotalInstalacion = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
          }

          totalInstalacion += subtotalInstalacion;
          if (currency === 'USD') {
            instalacionUSD += subtotalInstalacion;
          } else {
            instalacionARS += subtotalInstalacion;
          }
        }
      }
    });

    // Los fletes siempre son en ARS
    const totalFletes = formData.freights.reduce((sum, freight) => sum + freight.monto, 0);

    // Descuentos (CLAUDE.md §8.7 outlet model): Materiales = descuento por
    // línea (`descuento_linea`, fracción), sin descuento de sección; MO/Flete
    // = un % de sección (formData.descuento_instalacion/fletes).
    const materialesSubtotal = formData.environments.reduce((total, env) => {
      const subtotalEnv = env.materials
        .filter(m => m.currency === 'USD')
        .reduce((sum, mat) => sum + (mat.unidades * mat.precio_unitario * (1 - (mat.descuento_linea || 0))), 0);
      return total + subtotalEnv;
    }, 0);
    const materialesConDescuento = materialesSubtotal;
    const instalacionConDescuento = totalInstalacion * (1 - formData.descuento_instalacion / 100);
    const instalacionUSDConDescuento = instalacionUSD * (1 - formData.descuento_instalacion / 100);
    const instalacionARSConDescuento = instalacionARS * (1 - formData.descuento_instalacion / 100);
    const fletesConDescuento = totalFletes * (1 - formData.descuento_fletes / 100);

    // CLAUDE.md §6: Materiales se muestra como "US$ + IVA" en un solo número.
    // Misma fórmula que BudgetService.get_version_section_summary
    // ("materiales_total_con_iva"). `materialesUSDConIva` aplica la misma
    // fórmula sobre el bruto (materialesUSD), solo para la línea "Sin descuento".
    const materialesConIva = materialesConDescuento * (1 + ivaPercentage / 100);
    const materialesUSDConIva = materialesUSD * (1 + ivaPercentage / 100);

    return {
      materialesUSD,
      materialesARS,
      materialesSubtotal,
      materialesConDescuento,
      materialesConIva,
      materialesUSDConIva,
      instalacion: totalInstalacion,
      instalacionUSD,
      instalacionARS,
      instalacionConDescuento,
      instalacionUSDConDescuento,
      instalacionARSConDescuento,
      fletes: totalFletes,
      fletesConDescuento,
      totalUSD: materialesUSD,
      totalARS: materialesARS + totalInstalacion + totalFletes
    };
  };

  const formatCurrency = (amount: number, type: 'material' | 'flete' | 'instalacion' = 'material') => {
    // Siempre mostrar símbolo antes del valor, miles con punto y decimales con coma
    if (type === 'flete' || type === 'instalacion') {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Formatear moneda específica para installation services
  const formatInstallationCurrency = (amount: number, currency: string) => {
    if (currency === 'USD') {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // Mano de Obra puede tener líneas en ARS y en USD a la vez (CLAUDE.md
  // §8.5, restaurado 2026-09-01) - nunca mostrar un solo número mezclando
  // ambas monedas crudas; mostrar cada parte presente por separado.
  const formatInstalacionBreakdown = (usdAmount: number, arsAmount: number) => {
    const parts: string[] = [];
    if (arsAmount > 0) parts.push(formatInstallationCurrency(arsAmount, 'ARS'));
    if (usdAmount > 0) parts.push(formatInstallationCurrency(usdAmount, 'USD'));
    return parts.join(' + ') || formatInstallationCurrency(0, 'ARS');
  };

  // Manejar envío del formulario
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!formData.proyecto.trim()) {
      newErrors.proyecto = 'El nombre del proyecto es requerido';
    }
    
    if (!formData.cliente.trim()) {
      newErrors.cliente = 'El nombre del cliente es requerido';
    }
    
    if (!formData.vendedor.trim()) {
      newErrors.vendedor = 'El nombre del vendedor es requerido';
    }
    
    if (formData.email_cliente && !/\S+@\S+\.\S+/.test(formData.email_cliente)) {
      newErrors.email_cliente = 'El email no tiene un formato válido';
    }
    
    if (formData.email_vendedor && !/\S+@\S+\.\S+/.test(formData.email_vendedor)) {
      newErrors.email_vendedor = 'El email no tiene un formato válido';
    }
    
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Item D: no fallar en silencio - abrir un diálogo con el detalle de
      // los campos inválidos, además del resaltado inline de cada campo.
      setSubmitErrorDialog({ open: true, messages: Object.values(newErrors) });
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      
      // 1. Crear el presupuesto básico
      const budgetData: BudgetCreate = {
        proyecto: formData.proyecto,
        cliente: formData.cliente,
        responsable: formData.responsable,
        email_cliente: formData.email_cliente,
        tel_cliente: formData.tel_cliente
      };
      
      const newBudget = await BudgetApiService.createBudget(budgetData);
      
      // Si el título de la versión está vacío, usar el nombre del proyecto
      const versionTitle = formData.version_titulo.trim() ? 
        formData.version_titulo : 
        formData.proyecto;
      

      // 2. Crear la primera versión
      const versionData: BudgetVersionCreate = {
        titulo: versionTitle,
        vendedor: formData.vendedor,
        email_vendedor: formData.email_vendedor,
        tel_vendedor: formData.tel_vendedor,
        condiciones_contratacion: formData.condiciones_contratacion,
        condiciones_adicionales: formData.condiciones_adicionales,
        descuento_instalacion: formData.descuento_instalacion,
        descuento_fletes: formData.descuento_fletes,
        nombre: versionTitle,
        condiciones: formData.condiciones_contratacion,
        price_list_id: formData.price_list_id
      };


      const newVersion = await BudgetApiService.createBudgetVersion(newBudget.id, versionData);

      // Red de seguridad (fix/mo-flete-staged-row-capture): una fila inline de
      // Mano de Obra / Flete que el rep llenó pero NO confirmó con su botón
      // "Agregar" vive todavía en `newInstallation`/`newFreight` (estado staged),
      // fuera de `formData`. Antes se descartaba en silencio al crear y la
      // sección volvía vacía al reabrir. Acá la detectamos y la incluimos en el
      // payload aplicando las MISMAS validaciones/normalización que
      // handleAddInstallation/handleAddFreight (setState es async, así que NO
      // llamamos al add-handler y leemos formData en el mismo tick: computamos el
      // efectivo = comprometido + staged y construimos el payload de ahí).
      const stagedInst = stagedInstallationHasData(newInstallation, installationMode)
        ? normalizeStagedInstallation(newInstallation, installationMode)
        : null;
      const effectiveEnvironments = computeEffectiveInstallationEnvironments(
        formData.environments,
        stagedInst,
        selectedEnvironmentIndex,
        {
          generalEnvName: GENERAL_INSTALLATIONS_ENV_NAME,
          makeGeneralEnv: (inst) => ({
            nombre: GENERAL_INSTALLATIONS_ENV_NAME,
            descripcion: 'Instalaciones generales del presupuesto',
            orden: 9999,
            descuento_materiales: 0,
            materials: [],
            installations: [inst],
          }),
        },
      );
      const effectiveFreights = computeEffectiveFreights(
        formData.freights,
        stagedFreightHasData(newFreight, freightMode) ? { ...newFreight } : null,
      );

      // 3. Guardar TODO el estado anidado (ambientes + materiales + instalaciones
      // + fletes) en UNA sola llamada transaccional: el mismo endpoint atómico
      // (PUT .../versions/{id}/full -> save_full_version) que ya usa la pantalla
      // de edición. Reemplaza la vieja secuencia de POSTs por ítem, donde un
      // fallo parcial (MO y Fletes se creaban al final) perdía silenciosamente
      // esas secciones y dejaba los totales en 0 al reabrir el presupuesto
      // (PO items #10/#6, data loss). save_full_version reconcilia todo, recalcula
      // los totales y hace un solo commit: o se guarda todo, o no se guarda nada.
      const fullSavePayload: BudgetVersionFullSave = {
        titulo: versionTitle,
        vendedor: formData.vendedor,
        email_vendedor: formData.email_vendedor,
        tel_vendedor: formData.tel_vendedor,
        condiciones_contratacion: formData.condiciones_contratacion,
        condiciones_adicionales: formData.condiciones_adicionales,
        descuento_instalacion: formData.descuento_instalacion,
        descuento_fletes: formData.descuento_fletes,
        price_list_id: formData.price_list_id,
        // `nombre_version` es la etiqueta editable por versión (field report #16),
        // que DEBE quedar nula cuando el usuario no puso un nombre propio: así el
        // encabezado cae a "Versión N". NO usar el fallback al nombre del proyecto
        // (versionTitle) - eso hacía que "Versión 1" apareciera como el nombre del
        // proyecto. Solo se setea si el usuario tipeó un título explícito.
        nombre_version: formData.version_titulo.trim() || undefined,
        environments: effectiveEnvironments.map(environment => ({
          nombre: environment.nombre,
          orden: environment.orden,
          // % "Aplicar descuento" del ambiente (CLAUDE.md §8.7 outlet model).
          descuento_materiales: environment.descuento_materiales || 0,
          // Solo persistir materiales reales (in-situ o con producto de catálogo);
          // filas vacías/incompletas se descartan, igual que la vieja secuencia.
          materials: environment.materials
            .filter(material => material.es_in_situ || material.product_id)
            .map(material => ({
              product_id: material.es_in_situ ? undefined : material.product_id,
              descripcion: material.descripcion,
              codigo: material.codigo,
              unidades: material.unidades,
              // Solo incluir m2 si tiene valor (los materiales de estructura no tienen).
              metros2: material.metros_cuadrados || undefined,
              precio_unitario: material.precio_unitario,
              monto: material.unidades * material.precio_unitario,
              currency: material.currency,
              es_in_situ: material.es_in_situ,
              con_desperdicio: material.con_desperdicio || false,
              con_estructura: material.con_estructura || false,
              // Descuento aplicado de la línea (CLAUDE.md §8.7). NO se envía
              // `descuento_producto` (lo gestiona el servidor).
              descuento_linea: material.descuento_linea || 0,
              observaciones: material.observaciones,
            })),
          installations: (environment.installations || []).map(installation => {
            // Misma lógica de precio/subtotal que la vieja creación por ítem:
            // cada variante ya es su propia fila del catálogo con su precio
            // (CLAUDE.md §7, decidido 2026-08-30).
            let mano_obra_m2_final = installation.mano_obra_m2;
            let subtotalCalculado = 0;
            if (installation.installation_service) {
              if (!installation.mano_obra_m2 || installation.mano_obra_m2 === 0) {
                mano_obra_m2_final = installation.installation_service.precio;
              }
              if (mano_obra_m2_final) {
                subtotalCalculado = mano_obra_m2_final * (installation.cantidad_manual ?? 1);
              }
            } else if (installation.mano_obra_m2) {
              const esGlobal = installation.unidad?.toLowerCase() === 'global';
              subtotalCalculado = esGlobal
                ? installation.mano_obra_m2
                : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
            }
            return {
              installation_service_id: installation.installation_service_id,
              tipo_instalacion: installation.tipo_instalacion,
              cantidad_manual: installation.cantidad_manual,
              trabajo: installation.trabajo,
              descripcion_trabajo: installation.descripcion_trabajo,
              mano_obra_m2: mano_obra_m2_final,
              subtotal: subtotalCalculado,
              unidad: installation.unidad,
            };
          }),
        })),
        freights: effectiveFreights.map(freight => ({
          tipo_envio: freight.tipo_envio,
          descripcion_detalle: freight.descripcion_detalle,
          monto: freight.monto,
          currency: freight.currency,
          bonificado: false,
          // FK back to the FreightRate catalog row (CLAUDE.md §8.5) - undefined
          // para una entrada manual. `codigo` lo copia el backend desde la tarifa.
          freight_rate_id: freight.freight_rate_id,
        })),
        // Token de idempotencia: un reintento con el mismo token que un guardado
        // ya aplicado es un no-op server-side (cubre la respuesta perdida).
        save_token: (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };

      await BudgetApiService.saveFullVersion(newBudget.id, newVersion.id, fullSavePayload);

      // Limpiar el estado staged tras un guardado exitoso, para que la fila
      // inline recién volcada no se vuelva a agregar en un re-guardado
      // (fix/mo-flete-staged-row-capture, req. #2).
      setNewInstallation({});
      setInstallationMode('catalog');
      setNewFreight({ tipo_envio: '', monto: 0, tipo: 'fijo', currency: 'ARS' });
      setFreightMode('catalog');

      // Guardado exitoso: este es el propio redirect post-guardado, no un
      // abandono con cambios sin guardar - desactivar el guard explícitamente
      // antes de navegar, porque el patch de pushState corre sincrónicamente
      // dentro de navigate() y no espera al cleanup del efecto del guard.
      setNavigationBlocked(false);
      navigate(`/budgets/${newBudget.id}`);
      
    } catch (err: any) {
      console.error('❌ Error creating budget:', err);
      console.error('Error details:', err.response?.data);
      const backendMsg = err.response?.data?.detail || err.message || 'Error al crear el presupuesto';
      const message = typeof backendMsg === 'string' ? backendMsg : JSON.stringify(backendMsg);
      // Item D: mismo trato prominente para el rechazo del backend.
      setError(message);
      setSubmitErrorDialog({ open: true, messages: [message] });
    } finally {
      setLoading(false);
    }
  };

  // Efecto para manejar la alerta de stock
  useEffect(() => {
    if (alertStock) {
      setShowAlertStock(true);
      const timer = setTimeout(() => {
        setShowAlertStock(false);
        setAlertStock(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alertStock]);

  const totals = calculateTotals();

  return (
    <Box p={3}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/budgets')}
          sx={{ mb: 2 }}
        >
          Volver a Presupuestos
        </Button>
        
        {/* Título editable centrado */}
        <Box textAlign="center" mb={3}>
          <TextField
            fullWidth
            variant="standard"
            InputProps={{ 
              style: { fontSize: '2rem', fontWeight: 'bold', textAlign: 'center' },
              disableUnderline: true
            }}
            placeholder={formData.proyecto ? `${formData.proyecto} (título por defecto)` : "Título del Presupuesto"}
            value={formData.version_titulo}
            onChange={handleChange('version_titulo')}
            error={!!errors.version_titulo}
          />
        </Box>
        
        {/* Información no editable */}
        <Box display="flex" justifyContent="center" gap={4} mb={3}>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>ID:</strong> Se asignará al guardar
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>Versión:</strong> 1.0
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>Fecha:</strong> {new Date().toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Barra de acciones (arriba, sticky) - antes vivía al final del
              formulario; movida al tope para que crear/cancelar estén siempre
              a la vista sin scrollear (B4). */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper',
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate('/budgets')}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Presupuesto Completo'}
            </Button>
          </Box>

          {/* Nueva Tarjeta de Información Combinada */}
          <Card>
            <CardHeader 
              title="Información del Presupuesto"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Business color="primary" />}
            />
            <CardContent>
              <Box display="flex" flexWrap="wrap" gap={3}>
                {/* Columna izquierda - Información del Cliente */}
                <Box flex="1" minWidth="300px">
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'primary.main' }}>
                    Datos del Cliente
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nombre del Proyecto"
                      value={formData.proyecto}
                      onChange={handleChange('proyecto')}
                      error={!!errors.proyecto}
                      helperText={errors.proyecto || "Nombre del proyecto o identificador único"}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Nombre del Cliente"
                      value={formData.cliente}
                      onChange={handleChange('cliente')}
                      error={!!errors.cliente}
                      helperText={errors.cliente}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Responsable del Proyecto"
                      value={formData.responsable}
                      onChange={handleChange('responsable')}
                      helperText="Persona responsable del proyecto (opcional)"
                    />
                    
                    <TextField
                      fullWidth
                      label="Email del Cliente"
                      type="email"
                      value={formData.email_cliente}
                      onChange={handleChange('email_cliente')}
                      error={!!errors.email_cliente}
                      helperText={errors.email_cliente}
                    />
                    
                    <TextField
                      fullWidth
                      label="Teléfono del Cliente"
                      value={formData.tel_cliente}
                      onChange={handleChange('tel_cliente')}
                    />

                    {/* Oculto pero movido aquí para mantener consistencia con el título */}
                    <TextField
                      fullWidth
                      label="Título de la Versión"
                      value={formData.version_titulo}
                      onChange={handleChange('version_titulo')}
                      error={!!errors.version_titulo}
                      helperText={errors.version_titulo}
                      sx={{ display: 'none' }}
                    />
                  </Stack>
                </Box>
                
                {/* Columna derecha - Información del Vendedor */}
                <Box flex="1" minWidth="300px">
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'primary.main' }}>
                    Datos del Vendedor
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nombre del Vendedor"
                      value={formData.vendedor}
                      onChange={handleChange('vendedor')}
                      error={!!errors.vendedor}
                      helperText={errors.vendedor}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Email del Vendedor"
                      type="email"
                      value={formData.email_vendedor}
                      onChange={handleChange('email_vendedor')}
                      error={!!errors.email_vendedor}
                      helperText={errors.email_vendedor}
                    />
                    
                    <TextField
                      fullWidth
                      label="Teléfono del Vendedor"
                      value={formData.tel_vendedor}
                      onChange={handleChange('tel_vendedor')}
                    />

                    {/* Lista de precios (CLAUDE.md §4): gobierna qué
                        productos de Materiales se pueden agregar y con qué
                        precio - solo se puede elegir entre las asignadas. */}
                    <FormControl fullWidth>
                      <InputLabel>Lista de Precios</InputLabel>
                      <Select
                        value={formData.price_list_id ?? ''}
                        label="Lista de Precios"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          // IDs empiezan en 1, así que un valor "falsy" (incluido el ''
                          // del MenuItem "Sin lista asignada") siempre significa "ninguna".
                          price_list_id: e.target.value ? Number(e.target.value) : undefined
                        }))}
                      >
                        <MenuItem value="">
                          <em>Sin lista asignada</em>
                        </MenuItem>
                        {myPriceLists.map(pl => (
                          <MenuItem key={pl.id} value={pl.id}>{pl.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!formData.price_list_id && (
                      <Alert severity="warning">
                        Sin una lista de precios elegida no se pueden agregar materiales en
                        Ambientes - elegí una lista para habilitar la carga.
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 3. Tarjeta de Ambientes */}
          <Card>
            <CardHeader 
              title="Ambientes y Materiales"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Home color="primary" />}
              subheader={`${formData.environments.length} ambiente(s) configurado(s)`}
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setEnvironmentDialog(true)}
                >
                  Agregar Ambiente
                </Button>
              }
            />
            <CardContent>
              {formData.environments.length > 0 ? (
                <Box>
                  {formData.environments
                    .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                    .map((env, envIndex, filteredEnvs) => {
                      // Obtener el índice real en formData.environments
                      const actualEnvIndex = formData.environments.findIndex(e => e === env);
                      const isAddingHere = materialDialog && selectedEnvironmentIndex === actualEnvIndex;
                      // Subtotal del ambiente neto del descuento de cada línea
                      // (`descuento_linea`, CLAUDE.md §8.7 outlet model), y la
                      // suma en $ de esos descuentos (línea "Descuento"). Se
                      // calcula en el cliente para que reaccione en vivo.
                      const descuentoProductosUsd = env.materials
                        .filter(m => m.currency === 'USD')
                        .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario *
                          (mat.descuento_linea || 0)), 0);
                      const subtotalUsd = env.materials
                        .filter(m => m.currency === 'USD')
                        .reduce((total, mat) => total + mat.unidades * mat.precio_unitario, 0) - descuentoProductosUsd;
                      const descuentoTildado = env.descuentoTildado || (env.descuento_materiales || 0) > 0;
                      const setAmbientePct = (pct: number, tildado: boolean) => setFormData(prev => ({
                        ...prev,
                        environments: prev.environments.map((e, i) => i === actualEnvIndex
                          ? { ...applyAmbienteDiscount(e, pct), descuentoTildado: tildado } : e),
                      }));
                      const materialesCap = getMaterialesDiscountCap(formData.price_list_id);
                      return (
                        <Box key={actualEnvIndex} sx={{ mb: 4 }}>
                          {/* Encabezado del ambiente (antes era una fila de tabla con colspan;
                              ahora es un subtítulo propio arriba de la tabla de ESTE ambiente,
                              cada ambiente tiene su propia tabla en vez de compartir una sola). */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {editingEnvironmentIndex === actualEnvIndex ? (
                                // Modo edición: TextField con botones
                                <Box display="flex" alignItems="center" gap={1}>
                                  <TextField
                                    size="small"
                                    value={editingEnvironmentName}
                                    onChange={(e) => setEditingEnvironmentName(e.target.value)}
                                    autoFocus
                                    sx={{ minWidth: 200 }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleSaveEnvironmentName(actualEnvIndex)}
                                    disabled={!editingEnvironmentName.trim()}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleCancelEditEnvironmentName}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Box>
                              ) : (
                                // Modo visualización: Nombre con ícono de editar
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="h6" fontWeight="medium" sx={{ color: '#2e7d32' }}>
                                    {env.nombre}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStartEditEnvironmentName(actualEnvIndex, env.nombre)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  {env.descripcion && (
                                    <Chip
                                      label="Ver descripción"
                                      size="small"
                                      variant="outlined"
                                      onClick={() => alert(env.descripcion)}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              {/* "Aplicar descuento" del ambiente (CLAUDE.md §8.7 outlet
                                  model): llena el % de cada línea no-outlet. */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={descuentoTildado}
                                    onChange={(e) => setAmbientePct(0, e.target.checked)}
                                  />
                                }
                                label="Aplicar descuento"
                              />
                              {descuentoTildado && (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={env.descuento_materiales || ''}
                                  onChange={(e) => setAmbientePct(Math.min(Math.max(Number(e.target.value) || 0, 0), 99.99), true)}
                                  inputProps={{ min: 0, max: 99.99, step: 1, style: { width: '60px', textAlign: 'right' } }}
                                  InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                                />
                              )}
                              {materialesCap !== null && env.materials.some(m => !isOutletLine(m) && (m.descuento_linea || 0) * 100 > materialesCap) && (
                                <Typography variant="caption" color="warning.main" sx={{ maxWidth: 260 }}>
                                  Supera tu tope de descuento ({materialesCap}%): requerirá aprobación al enviar.
                                </Typography>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteEnvironment(actualEnvIndex)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Box>

                          {!formData.price_list_id && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              Elegí una lista de precios (Datos del Vendedor, arriba) para poder
                              agregar materiales en este ambiente.
                            </Alert>
                          )}

                          <TableContainer component={Paper}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio Unitario</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Unidades</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>M2</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Descuento</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {/* "+" inline en vez del botón "Agregar Material" del
                                    header (Phase 8.7 Round A, item 3) - ocupa el
                                    lugar de la primera fila de ítem de esta tabla. */}
                                {!isAddingHere && (
                                  <TableRow>
                                    <TableCell colSpan={8} align="right">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={!formData.price_list_id}
                                        title="Agregar Material"
                                        onClick={() => {
                                          // Bloqueo real (ver también el guard en
                                          // handleAddMaterial): sin lista de precios
                                          // elegida no hay picker que gatear ni precio
                                          // que resolver, así que ni siquiera se abre
                                          // la fila de alta inline (CLAUDE.md §4).
                                          if (!formData.price_list_id) return;
                                          setSelectedEnvironmentIndex(actualEnvIndex);
                                          setMaterialDialog(true);
                                        }}
                                      >
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {env.materials.length === 0 && !isAddingHere && (
                                  <TableRow>
                                    <TableCell colSpan={8} align="center">
                                      <Typography variant="body2" color="text.secondary">
                                        No hay materiales agregados
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {/* Filas de materiales */}
                                {env.materials.map((material, matIndex) => {
                                  // M2: lo que el usuario cargó, + el 10% de desperdicio
                                  // cuando ese toggle está activo (spec de esta tarea) -
                                  // los materiales de estructura/in-situ no tienen m2.
                                  const m2Display = material.metros_cuadrados
                                    ? Math.round((material.con_desperdicio ? material.metros_cuadrados * 1.1 : material.metros_cuadrados) * 100) / 100
                                    : null;
                                  return (
                                    <TableRow key={`material-${actualEnvIndex}-${matIndex}`}>
                                      <TableCell>{material.codigo}</TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                          {material.es_in_situ ? (
                                            <Chip label="Manual" size="small" color="warning" />
                                          ) : material.product?.is_outlet ? (
                                            <Chip icon={<LocalOffer />} label="Outlet" size="small" color="warning" />
                                          ) : null}
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.descripcion}
                                          </Typography>
                                          {material.observaciones?.includes('Material de estructura') && (
                                            <Chip
                                              label="Estructura"
                                              size="small"
                                              color="secondary"
                                              variant="outlined"
                                            />
                                          )}
                                        </Box>
                                        {material.observaciones && !material.observaciones.includes('Material de estructura') && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {material.observaciones}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatCurrency(material.precio_unitario, material.currency === 'ARS' ? 'instalacion' : 'material')}
                                      </TableCell>
                                      <TableCell align="right">
                                        <TextField
                                          type="number"
                                          size="small"
                                          value={material.unidades}
                                          onChange={(e) => {
                                            const newValue = parseInt(e.target.value) || 1;
                                            if (newValue < 1) return; // Validación mínima

                                            setFormData(prev => {
                                              const newEnvironments = [...prev.environments];
                                              const materialsArr = [...newEnvironments[actualEnvIndex].materials];
                                              // Sincroniza m2 con la nueva cantidad y recalcula las
                                              // filas de estructura de esta línea (item 4).
                                              materialsArr[matIndex] = syncM2ToUnidades(materialsArr[matIndex], newValue);
                                              const rebuilt = rebuildStructureRows(materialsArr, matIndex);
                                              newEnvironments[actualEnvIndex] = { ...newEnvironments[actualEnvIndex], materials: rebuilt };
                                              return { ...prev, environments: newEnvironments };
                                            });
                                          }}
                                          sx={{ width: '90px' }}
                                          inputProps={{
                                            min: 1,
                                            style: { textAlign: 'right', padding: '8px' }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        {m2Display !== null ? `${m2Display} m²` : '-'}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatCurrency(material.unidades * material.precio_unitario, material.currency === 'ARS' ? 'instalacion' : 'material')}
                                      </TableCell>
                                      {/* Descuento de la línea (CLAUDE.md §8.7 outlet model):
                                          outlet -> prellenado, sólo hacia abajo (max = descuento
                                          de producto); no-outlet -> visible cuando el ambiente
                                          tiene "Aplicar descuento" tildado. */}
                                      <TableCell align="center">
                                        {(isOutletLine(material) ? (material.descuento_producto || 0) > 0 : descuentoTildado) && (
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={Math.round((material.descuento_linea || 0) * 10000) / 100}
                                            onChange={(e) => {
                                              const value = clampLineDiscount(material, Number(e.target.value));
                                              setFormData(prev => {
                                                const newEnvironments = [...prev.environments];
                                                newEnvironments[actualEnvIndex].materials[matIndex] = {
                                                  ...newEnvironments[actualEnvIndex].materials[matIndex], descuento_linea: value,
                                                };
                                                return { ...prev, environments: newEnvironments };
                                              });
                                            }}
                                            inputProps={{
                                              min: 0,
                                              max: isOutletLine(material) ? Math.round((material.descuento_producto || 0) * 10000) / 100 : 99.99,
                                              step: 1,
                                              style: { width: '56px', textAlign: 'right', padding: '6px' },
                                            }}
                                            InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEditMaterial(actualEnvIndex, matIndex)}
                                          color="primary"
                                          title="Editar"
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteMaterial(actualEnvIndex, matIndex)}
                                          color="error"
                                          title="Eliminar"
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}

                                {/* Fila de alta inline de material (reemplaza el Dialog
                                    modal anterior - CLAUDE.md/tarea: "convert from a popup
                                    to an inline row"). Mismo contenido/lógica que tenía el
                                    Dialog, ahora embebido en una fila de esta misma tabla. */}
                                {isAddingHere && (
                                  <TableRow>
                                    <TableCell colSpan={8} sx={{ bgcolor: 'grey.50', verticalAlign: 'top' }}>
                                      <Stack spacing={2} sx={{ py: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                          {editingMaterialIndex ? 'Editar' : 'Agregar'} Material - {env.nombre}
                                        </Typography>

                                        {/* Selección de producto existente primero */}
                                        <Autocomplete
                                          openOnFocus
                                          options={products}
                                          getOptionLabel={(option: Product) => `${option.codigo} ${option.sku ? `- ${option.sku}` : ''} ${option.color ? `(${option.color})` : ''}`}
                                          getOptionDisabled={(option: Product) => isProductGated(option)}
                                          value={!newMaterial.es_in_situ ? (newMaterial.product || null) : null}
                                          onChange={(event, newValue) => handleProductSelect(newValue)}
                                          disabled={newMaterial.es_in_situ}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              autoFocus
                                              label="Seleccionar Producto *"
                                              placeholder="Buscar por descripción, código, color o grupo..."
                                              helperText={
                                                priceMap !== null
                                                  ? `Precios según ${myPriceLists.find(pl => pl.id === formData.price_list_id)?.name || 'la lista elegida'} - los productos sin precio en esa lista aparecen deshabilitados.`
                                                  : undefined
                                              }
                                            />
                                          )}
                                          renderOption={(props: any, option: Product) => {
                                            const gated = isProductGated(option);
                                            return (
                                              <Box component="li" {...props}>
                                                <Box>
                                                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                      {option.descripcion || option.codigo}
                                                    </Typography>
                                                    {option.is_outlet && (
                                                      <Chip label="Outlet" size="small" color="warning" />
                                                    )}
                                                    {gated && (
                                                      <Chip
                                                        label={`Sin precio en ${myPriceLists.find(pl => pl.id === formData.price_list_id)?.name || 'esta lista'}`}
                                                        size="small"
                                                        color="default"
                                                      />
                                                    )}
                                                  </Box>
                                                  <Typography variant="caption" color="text.secondary">
                                                    Código: {option.codigo} | Grupo: {option.grupo || 'Sin grupo'} | Color: {option.color || 'Sin color'} | Precio: {formatCurrency(gated ? 0 : (priceMap?.[option.id]?.price ?? 0))}
                                                  </Typography>
                                                  {option.largo && option.ancho && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      Dimensiones: {option.largo} × {option.ancho} {option.espesor ? `× ${option.espesor}` : ''}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            );
                                          }}
                                          filterOptions={(options: Product[], { inputValue }: { inputValue: string }) => {
                                            if (!inputValue) return options;
                                            const lowerInput = inputValue.toLowerCase();
                                            return options.filter((option: Product) =>
                                              (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput)) ||
                                              (option.grupo && option.grupo.toLowerCase().includes(lowerInput)) ||
                                              option.codigo.toLowerCase().includes(lowerInput) ||
                                              (option.color && option.color.toLowerCase().includes(lowerInput)) ||
                                              (option.sku && option.sku.toLowerCase().includes(lowerInput))
                                            );
                                          }}
                                        />

                                        {/* Toggle para producto manual (abajo del selector) */}
                                        <Box>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={newMaterial.es_in_situ}
                                                onChange={(e) => setNewMaterial(prev => ({
                                                  ...prev,
                                                  es_in_situ: e.target.checked,
                                                  product_id: undefined,
                                                  product: undefined,
                                                  descripcion: '',
                                                  codigo: '',
                                                  precio_unitario: 0,
                                                  cantidad_calculada: 0,
                                                  metros_cuadrados: undefined,
                                                  usar_cantidad_manual: false,
                                                  con_desperdicio: true,
                                                  con_estructura: false,
                                                  observaciones: ''
                                                }))}
                                              />
                                            }
                                            label="Ingresar producto manual (no existente en catálogo)"
                                          />

                                          {newMaterial.es_in_situ && (
                                            <Alert severity="warning" sx={{ mt: 1 }}>
                                              Este producto no se creará en el Catálogo, solo aparecerá en este presupuesto
                                            </Alert>
                                          )}
                                        </Box>

                                        {!newMaterial.es_in_situ ? (
                                          // Selección de producto existente - opciones adicionales
                                          <>

                                            {newMaterial.product && (
                                              <>
                                                {/* Opciones de desperdicio y estructura */}
                                                <Box>
                                                  <Typography variant="subtitle2" gutterBottom>
                                                    Opciones del Producto:
                                                  </Typography>
                                                  <Stack spacing={1}>
                                                    <FormControlLabel
                                                      control={
                                                        <Checkbox
                                                          checked={newMaterial.con_desperdicio || false}
                                                          onChange={(e) => handleDesperdicioChange(e.target.checked)}
                                                        />
                                                      }
                                                      label="Incluir desperdicio (+10%)"
                                                    />
                                                    {newMaterial.product.estructura_m2 && (
                                                      <FormControlLabel
                                                        control={
                                                          <Checkbox
                                                            checked={newMaterial.con_estructura || false}
                                                            onChange={(e) => setNewMaterial(prev => ({
                                                              ...prev,
                                                              con_estructura: e.target.checked
                                                            }))}
                                                          />
                                                        }
                                                        label="Incluir estructura"
                                                      />
                                                    )}
                                                  </Stack>
                                                </Box>

                                                {/* Campos de m² y cantidad que se completan mutuamente */}
                                                <Box>
                                                  <Typography variant="subtitle2" gutterBottom>
                                                    Cálculo de Cantidad:
                                                  </Typography>
                                                  <Box display="flex" gap={2}>
                                                    <TextField
                                                      fullWidth
                                                      label="Metros Cuadrados"
                                                      type="number"
                                                      required
                                                      value={newMaterial.metros_cuadrados ?? ''}
                                                      onChange={(e) => {
                                                        // B6: vacío = undefined (no cae a 1). Sin m² y sin
                                                        // cantidad manual, el alta queda bloqueada abajo.
                                                        const m2 = e.target.value === '' ? undefined : (parseFloat(e.target.value) || undefined);
                                                        if (!newMaterial.usar_cantidad_manual) {
                                                          handleMetrosCuadradosChange(m2);
                                                        } else {
                                                          setNewMaterial(prev => ({
                                                            ...prev,
                                                            metros_cuadrados: m2
                                                          }));
                                                        }
                                                      }}
                                                      placeholder="Área a cubrir en m²"
                                                      inputProps={{ min: 0.1, step: 0.1 }}
                                                    />

                                                    <TextField
                                                      fullWidth
                                                      label="Cantidad (unidades)"
                                                      type="number"
                                                      value={(newMaterial.usar_cantidad_manual ? newMaterial.cantidad_manual : newMaterial.cantidad_calculada) || ''}
                                                      onChange={(e) => {
                                                        const cantidad = parseFloat(e.target.value) || 0;
                                                        setNewMaterial(prev => ({ ...prev, usar_cantidad_manual: true }));
                                                        handleCantidadManualChange(cantidad);
                                                      }}
                                                      placeholder="Cantidad específica"
                                                      inputProps={{ min: 0, step: 1 }}
                                                    />
                                                  </Box>
                                                </Box>

                                                {newMaterial.cantidad_calculada && newMaterial.cantidad_calculada > 0 && !newMaterial.usar_cantidad_manual && (
                                                  <Alert severity="info">
                                                    <Typography variant="body2">
                                                      Cantidad calculada automáticamente: <strong>{newMaterial.cantidad_calculada} unidades</strong>
                                                      {newMaterial.con_desperdicio && <span> (incluye 10% desperdicio)</span>}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Basado en {newMaterial.metros_cuadrados}m² × {newMaterial.product.factor_conversion_m2} unidades/m²
                                                    </Typography>
                                                  </Alert>
                                                )}

                                                {newMaterial.usar_cantidad_manual && newMaterial.cantidad_manual && newMaterial.product && (
                                                  <Alert severity="success">
                                                    <Typography variant="body2">
                                                      Metros cuadrados calculados automáticamente: <strong>{newMaterial.metros_cuadrados}m²</strong>
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Basado en {newMaterial.cantidad_manual} unidades ÷ {newMaterial.product.factor_conversion_m2} unidades/m²
                                                      {newMaterial.con_desperdicio && <span> (considerando desperdicio incluido)</span>}
                                                    </Typography>
                                                  </Alert>
                                                )}

                                                <FormControlLabel
                                                  control={
                                                    <Checkbox
                                                      checked={newMaterial.usar_cantidad_manual || false}
                                                      onChange={(e) => {
                                                        const useManual = e.target.checked;
                                                        setNewMaterial(prev => ({
                                                          ...prev,
                                                          usar_cantidad_manual: useManual,
                                                          unidades: useManual ? (prev.cantidad_manual || 0) : (prev.cantidad_calculada || 0)
                                                        }));
                                                      }}
                                                    />
                                                  }
                                                  label="Usar cantidad manual (deshabilita cálculo automático)"
                                                />

                                                {/* Campo de observaciones */}
                                                <TextField
                                                  fullWidth
                                                  label="Observaciones"
                                                  value={newMaterial.observaciones || ''}
                                                  onChange={(e) => setNewMaterial(prev => ({
                                                    ...prev,
                                                    observaciones: e.target.value.slice(0, 100) // Limitar a 100 caracteres
                                                  }))}
                                                  placeholder="Observaciones sobre este material"
                                                  multiline
                                                  rows={2}
                                                  inputProps={{ maxLength: 100 }}
                                                  helperText={`${(newMaterial.observaciones || '').length}/100 caracteres`}
                                                />

                                                {/* Vista previa de estructura si está marcada */}
                                                {newMaterial.con_estructura && newMaterial.product.estructura_m2 && (
                                                  <Alert severity="info">
                                                    <Typography variant="subtitle2" gutterBottom>
                                                      ⚠️ Estructura incluida
                                                    </Typography>
                                                    <Typography variant="body2" gutterBottom>
                                                      {newMaterial.product.grupo === 'PERFIL'
                                                        ? `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por unidad (total unidades: ${newMaterial.unidades || 1}).`
                                                        : `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por metro cuadrado (total m²: ${newMaterial.metros_cuadrados || 1}).`}
                                                    </Typography>
                                                    <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                                                      {newMaterial.product.estructura_m2.split(/[;\n]+/).map((entry, index) => {
                                                        const trimmedEntry = entry.trim();
                                                        if (!trimmedEntry) return null;
                                                        try {
                                                          const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
                                                          const codigoTrimmed = codigo.trim();
                                                          const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
                                                          const structureProduct = products.find((p: Product) => p.codigo === codigoTrimmed);
                                                          let cantidadBase, cantidadTotal, cantidadFinal, detalleCalculo;
                                                          if (newMaterial.product && newMaterial.product.grupo === 'PERFIL') {
                                                            cantidadBase = newMaterial.unidades || 1;
                                                            cantidadTotal = cantidadPorM2 * cantidadBase;
                                                            cantidadFinal = Math.ceil(cantidadTotal);
                                                            detalleCalculo = `(${cantidadPorM2}/unidad × ${cantidadBase} unidades)`;
                                                          } else {
                                                            cantidadBase = newMaterial.metros_cuadrados || 1;
                                                            cantidadTotal = cantidadPorM2 * cantidadBase;
                                                            cantidadFinal = Math.ceil(cantidadTotal);
                                                            detalleCalculo = `(${cantidadPorM2}/m² × ${cantidadBase}m²)`;
                                                          }
                                                          return (
                                                            <Typography
                                                              key={index}
                                                              component="li"
                                                              variant="caption"
                                                              color="text.secondary"
                                                            >
                                                              <strong>{codigoTrimmed}</strong>
                                                              {structureProduct && ` (${structureProduct.sku || 'Sin SKU'})`}:
                                                              {' '}{cantidadFinal} unidades
                                                              <span style={{ color: '#666' }}>
                                                                {' '}{detalleCalculo}
                                                              </span>
                                                              <span style={{ color: '#888', fontSize: '0.8em' }}>
                                                                {' '}(sin desperdicio)
                                                              </span>
                                                            </Typography>
                                                          );
                                                        } catch {
                                                          return null;
                                                        }
                                                      })}
                                                    </Box>
                                                  </Alert>
                                                )}
                                              </>
                                            )}
                                          </>
                                        ) : (
                                          // Creación de material in situ
                                          <>
                                            <TextField
                                              fullWidth
                                              label="Código *"
                                              value={newMaterial.codigo}
                                              onChange={(e) => setNewMaterial(prev => ({
                                                ...prev,
                                                codigo: e.target.value
                                              }))}
                                              placeholder="Código único del material"
                                              required
                                            />

                                            <TextField
                                              fullWidth
                                              label="Descripción *"
                                              value={newMaterial.descripcion}
                                              onChange={(e) => setNewMaterial(prev => ({
                                                ...prev,
                                                descripcion: e.target.value
                                              }))}
                                              placeholder="Descripción detallada del material"
                                              required
                                            />

                                            <Box display="flex" gap={2}>
                                              <TextField
                                                fullWidth
                                                label="Unidades"
                                                type="number"
                                                value={newMaterial.unidades || ''}
                                                onChange={(e) => setNewMaterial(prev => ({
                                                  ...prev,
                                                  unidades: parseFloat(e.target.value) || 0
                                                }))}
                                                placeholder="Cantidad"
                                              />

                                              <TextField
                                                fullWidth
                                                label="Precio Unitario"
                                                type="number"
                                                value={newMaterial.precio_unitario || ''}
                                                onChange={(e) => setNewMaterial(prev => ({
                                                  ...prev,
                                                  precio_unitario: parseFloat(e.target.value) || 0
                                                }))}
                                                placeholder="Precio por unidad"
                                              />

                                              <FormControl fullWidth>
                                                <InputLabel>Moneda</InputLabel>
                                                <Select
                                                  value={newMaterial.currency}
                                                  onChange={(e) => setNewMaterial(prev => ({
                                                    ...prev,
                                                    currency: e.target.value as 'USD' | 'ARS'
                                                  }))}
                                                  label="Moneda"
                                                >
                                                  <MenuItem value="USD">USD</MenuItem>
                                                  <MenuItem value="ARS">ARS</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </Box>
                                          </>
                                        )}

                                        {/* Resumen del cálculo */}
                                        {(newMaterial.unidades > 0 && newMaterial.precio_unitario > 0) && (
                                          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                              Resumen del Material
                                            </Typography>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                              <Typography variant="body2">
                                                {newMaterial.unidades} unidades × {formatCurrency(newMaterial.precio_unitario)}
                                              </Typography>
                                              <Typography variant="h6" color="primary">
                                                {formatCurrency(newMaterial.unidades * newMaterial.precio_unitario)}
                                              </Typography>
                                            </Box>
                                            {newMaterial.product && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Producto: {newMaterial.product.codigo} {newMaterial.product.sku ? `(${newMaterial.product.sku})` : ''}
                                              </Typography>
                                            )}
                                          </Box>
                                        )}

                                        <Box display="flex" justifyContent="flex-end" gap={1}>
                                          <Button onClick={() => {
                                            setMaterialDialog(false);
                                            setSelectedEnvironmentIndex(null);
                                            setEditingMaterialIndex(null);
                                          }}>
                                            Cancelar
                                          </Button>
                                          <Button
                                            onClick={editingMaterialIndex ? handleUpdateMaterial : handleAddMaterial}
                                            variant="contained"
                                            disabled={
                                              newMaterial.es_in_situ
                                                ? (!newMaterial.codigo.trim() || !newMaterial.descripcion.trim())
                                                : (!newMaterial.product_id
                                                    || (!!newMaterial.product && isProductGated(newMaterial.product))
                                                    // B6: exige m² o cantidad; sin una cantidad resuelta > 0 no se agrega
                                                    || !((newMaterial.usar_cantidad_manual ? newMaterial.cantidad_manual : newMaterial.cantidad_calculada) ?? 0))
                                            }
                                          >
                                            {editingMaterialIndex ? 'Guardar Cambios' : 'Agregar Material'}
                                          </Button>
                                        </Box>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {/* Descuento del ambiente: $ de los descuentos por línea
                                    (CLAUDE.md §8.7 outlet model). Oculto si es 0. El
                                    valor va bajo la columna Total. */}
                                {descuentoProductosUsd > 0.005 && (
                                  <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
                                    <TableCell colSpan={5} align="right">
                                      Descuento:
                                    </TableCell>
                                    <TableCell align="right">
                                      - {formatCurrency(descuentoProductosUsd, 'material')}
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                  </TableRow>
                                )}

                                {/* Subtotal del ambiente (CLAUDE.md §8.7,
                                    2026-09-08): sólo el subtotal, con los
                                    descuentos de producto por línea ya
                                    aplicados. El descuento por ambiente se
                                    retiró - el descuento del rep es ahora un
                                    único % a nivel de sección (abajo). Sin
                                    total de Unidades. Valor bajo la columna Total
                                    (col 6 tras reordenar Descuento). */}
                                <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
                                  <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                                    Subtotal Materiales {env.nombre}:
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(subtotalUsd, 'material')}
                                  </TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Línea divisoria entre ambientes */}
                          {envIndex < filteredEnvs.length - 1 && (
                            <Divider sx={{ borderBottomWidth: 2, borderColor: '#e0e0e0', mt: 3 }} />
                          )}
                        </Box>
                      );
                    })}

                  {/* Footer de Materiales (CLAUDE.md §8.7 outlet model): sin descuento
                      de sección. Subtotal = suma de subtotales por ambiente (netos de
                      descuento por línea) -> Total + IVA (fondo negro/blanco). */}
                  {(() => {
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end', mt: 1 }}>
                        <Box sx={{ px: 3, py: 0.5, minWidth: 280, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography sx={{ fontWeight: 600 }}>Subtotal Materiales:</Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {formatCurrency(totals.materialesSubtotal, 'material')}
                          </Typography>
                        </Box>
                        {/* 4. Total (neto) + IVA - único con fondo (negro/blanco) */}
                        <Box sx={{ bgcolor: 'common.black', color: 'common.white', px: 3, py: 1, borderRadius: 1, minWidth: 280, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography sx={{ fontWeight: 600 }}>Total Materiales:</Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {formatCurrency(totals.materialesConDescuento, 'material')} + IVA
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              ) : (
                <Alert severity="info">
                  No hay ambientes configurados. Agregue el primer ambiente para comenzar.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Nueva Tarjeta de Instalación */}
          <Card>
            <CardHeader 
              title="Servicios de Instalación"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Build color="primary" />}
              subheader="Servicios de instalación para los ambientes"
            />
            <CardContent>
              {formData.environments.length > 0 ? (
                <Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ambiente</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio por Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Cantidad de Unidades</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* "+" inline en vez del botón "Agregar Instalación"
                            del header (Phase 8.7 Round A, item 3) - ocupa el
                            lugar de la primera fila de ítem de esta tabla. */}
                        {!installationDialog && (
                          <TableRow>
                            <TableCell colSpan={7} align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                title="Agregar Instalación"
                                onClick={() => {
                                  // Sin ambiente preseleccionado - se elige
                                  // en la fila inline que aparece abajo
                                  // (antes era un Dialog aparte).
                                  setSelectedEnvironmentIndex(null);
                                  setInstallationDialog(true);
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                        {formData.environments
                          .filter(env => env.installations && env.installations.length > 0)
                          .map((env, envIndex) => {
                            // Índice REAL en formData.environments (el
                            // "envIndex" de este .filter().map() es la
                            // posición dentro del arreglo FILTRADO, no el
                            // índice real - se usa tal cual más abajo para no
                            // tocar el flujo de borrado existente, pero la
                            // edición nueva de este batch usa el real).
                            const actualEnvIndex = formData.environments.findIndex(e => e === env);
                            // Calcular área total del ambiente
                            const areaTotal = env.materials.reduce((total, material) => {
                              return total + (material.metros_cuadrados || 0);
                            }, 0);
                            
                            // Renderizar una fila por cada instalación en el ambiente
                            return env.installations.map((installation, instIndex) => {
                              // Moneda por fila restaurada (CLAUDE.md §8.5,
                              // 2026-09-01): catálogo usa
                              // installation_service.currency, 'custom'
                              // sigue siendo siempre USD.
                              const installationCurrency = installation.installation_service
                                ? (installation.installation_service.currency || 'USD')
                                : 'USD';
                              let subtotal = 0;
                              if (installation.installation_service) {
                                const precio = installation.mano_obra_m2 || installation.installation_service.precio;
                                if (precio) {
                                  subtotal = precio * (installation.cantidad_manual || 1);
                                }
                              } else if (installation.mano_obra_m2) {
                                // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual || 1);
                              }
                              // PRECIO = 0 en el catálogo significa "Consultar"
                              // (CLAUDE.md §7) - no un precio real gratuito. Solo
                              // aplica sin un precio editado a mano en esta línea
                              // (un $0 editado a mano sigue siendo un $0 real,
                              // CLAUDE.md §6 permite edición manual en Mano de Obra).
                              const isConsultar = !!installation.installation_service &&
                                !installation.mano_obra_m2 &&
                                (installation.installation_service.precio || 0) === 0;

                              return (
                                <TableRow key={`installation-${envIndex}-${instIndex}`}>
                                  <TableCell>{env.nombre}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {installation?.installation_service
                                        ? `${installation.installation_service.descripcion}${installation.installation_service.complejidad ? ' - ' + installation.installation_service.complejidad : ''}`
                                        : installation?.trabajo || 'Instalación personalizada'}
                                    </Typography>
                                    {installation?.descripcion_trabajo && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {installation.descripcion_trabajo}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {installation?.unidad || installation?.installation_service?.unidad || ''}
                                  </TableCell>
                                  <TableCell align="right">
                                    {isConsultar ? (
                                      'Consultar'
                                    ) : installation?.unidad?.toLowerCase() === 'global' && !installation?.installation_service ? (
                                      '-'
                                    ) : (
                                      formatInstallationCurrency(
                                        installation?.mano_obra_m2 || installation?.installation_service?.precio || 0, installationCurrency
                                      )
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.cantidad_manual || areaTotal.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {isConsultar ? 'Consultar' : formatInstallationCurrency(subtotal, installationCurrency)}
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditInstallation(actualEnvIndex, instIndex)}
                                      color="primary"
                                      title="Editar"
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        // Borrado por REFERENCIA de ambiente (no
                                        // por el índice del arreglo FILTRADO por
                                        // installations.length > 0): con el
                                        // índice filtrado un ambiente anterior
                                        // sin instalaciones desplazaba el borrado
                                        // al ambiente equivocado (field report
                                        // #3, chip task_5e3f9db9).
                                        setFormData(prev => ({
                                          ...prev,
                                          environments: removeInstallationByEnvRef(prev.environments, env, instIndex),
                                        }));
                                      }}
                                      color="error"
                                      title="Eliminar"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            });
                        })}

                        {/* Fila inline para agregar una instalación - reemplaza
                            el Dialog "Agregar Instalación" que existía antes
                            (conversión a fila editable, 2026-09-05). Preserva
                            el toggle Catálogo/Personalizada, el picker de
                            installation_service, el fallback 'custom', y el
                            manejo de "Consultar" ($0 = precio a consultar). */}
                        {installationDialog && (() => {
                          const isCatalog = installationMode === 'catalog';
                          const rowCurrency = isCatalog
                            ? (newInstallation.installation_service?.currency || 'USD')
                            : 'USD';
                          const isConsultarRow = isCatalog &&
                            !!newInstallation.installation_service &&
                            !newInstallation.mano_obra_m2 &&
                            (newInstallation.installation_service.precio || 0) === 0;
                          const rowPrecio = isCatalog
                            ? calculateInstallationPrice()
                            : (newInstallation.mano_obra_m2 || 0);
                          const esGlobalRow = !isCatalog && newInstallation.unidad?.toLowerCase() === 'global';
                          const rowTotal = esGlobalRow ? rowPrecio : rowPrecio * (newInstallation.cantidad_manual || 0);
                          const disableSave =
                            selectedEnvironmentIndex === null ||
                            !newInstallation.cantidad_manual || newInstallation.cantidad_manual <= 0 ||
                            (isCatalog && !newInstallation.installation_service_id) ||
                            (!isCatalog && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2));

                          return (
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                                <FormControl fullWidth size="small" required>
                                  <InputLabel shrink>Ambiente</InputLabel>
                                  <Select
                                    label="Ambiente"
                                    displayEmpty
                                    notched
                                    value={selectedEnvironmentIndex !== null ? selectedEnvironmentIndex : ''}
                                    onChange={(e) => setSelectedEnvironmentIndex(Number(e.target.value))}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>Elegir...</em>
                                    </MenuItem>
                                    <MenuItem value={GENERAL_INSTALLATIONS_INDEX}>
                                      <em>Instalaciones Generales</em>
                                    </MenuItem>
                                    {formData.environments
                                      .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                                      .map((env) => {
                                        const realIndex = formData.environments.findIndex(e => e.nombre === env.nombre);
                                        return (
                                          <MenuItem key={realIndex} value={realIndex}>
                                            {env.nombre}
                                          </MenuItem>
                                        );
                                      })}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell sx={{ minWidth: 280, verticalAlign: 'top' }}>
                                <Stack spacing={1}>
                                  <ToggleButtonGroup
                                    value={installationMode}
                                    exclusive
                                    size="small"
                                    onChange={(e, newMode) => {
                                      if (newMode !== null) {
                                        setInstallationMode(newMode);
                                        setNewInstallation({});
                                      }
                                    }}
                                  >
                                    <ToggleButton value="catalog">Catálogo</ToggleButton>
                                    <ToggleButton value="custom">Personalizada</ToggleButton>
                                  </ToggleButtonGroup>
                                  {isCatalog ? (
                                    <Autocomplete
                                      key={moPickerTick}
                                      openOnFocus
                                      size="small"
                                      options={installationServices}
                                      getOptionLabel={(option: InstallationService) =>
                                        `${option.descripcion}${option.complejidad ? ' - ' + option.complejidad : ''} (${option.unidad})`
                                      }
                                      value={newInstallation.installation_service || null}
                                      onChange={(event, newValue) => handleInstallationServiceSelect(newValue)}
                                      loading={loadingInstallationServices}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          autoFocus
                                          label="Servicio de Instalación *"
                                          placeholder="Buscar por código, descripción..."
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                {loadingInstallationServices ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                      renderOption={(props: any, option: InstallationService) => (
                                        <Box component="li" {...props}>
                                          <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                              {option.descripcion}{option.complejidad ? ` - ${option.complejidad}` : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Unidad: {option.unidad} | Precio: {option.precio === 0 ? 'Consultar' : formatInstallationCurrency(option.precio, option.currency || 'USD')}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      )}
                                      filterOptions={(options: InstallationService[], { inputValue }: { inputValue: string }) => {
                                        if (!inputValue) return options;
                                        const lowerInput = inputValue.toLowerCase();
                                        return options.filter((option: InstallationService) =>
                                          option.descripcion.toLowerCase().includes(lowerInput) ||
                                          option.codigo.toLowerCase().includes(lowerInput) ||
                                          (option.complejidad && option.complejidad.toLowerCase().includes(lowerInput)) ||
                                          option.unidad.toLowerCase().includes(lowerInput)
                                        );
                                      }}
                                    />
                                  ) : (
                                    <>
                                      <TextField
                                        size="small"
                                        label="Nombre del trabajo *"
                                        value={newInstallation.trabajo || ''}
                                        onChange={(e) => setNewInstallation(prev => ({
                                          ...prev,
                                          trabajo: e.target.value,
                                          tipo_instalacion: 'custom' as TipoInstalacion
                                        }))}
                                        placeholder="Ej: Instalación de vidrio templado"
                                      />
                                      <FormControl size="small">
                                        <InputLabel>Unidad</InputLabel>
                                        <Select
                                          label="Unidad"
                                          value={newInstallation.unidad || ''}
                                          onChange={(e) => setNewInstallation(prev => ({
                                            ...prev,
                                            unidad: e.target.value
                                          }))}
                                        >
                                          <MenuItem value="m2">m2</MenuItem>
                                          <MenuItem value="ml">ml (metro lineal)</MenuItem>
                                          <MenuItem value="unit">Unidad</MenuItem>
                                          <MenuItem value="global">Global</MenuItem>
                                          <MenuItem value="hora">Hora</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </>
                                  )}
                                  <TextField
                                    size="small"
                                    label="Nota (opcional)"
                                    value={newInstallation.descripcion_trabajo || ''}
                                    onChange={(e) => setNewInstallation(prev => ({
                                      ...prev,
                                      descripcion_trabajo: e.target.value
                                    }))}
                                    placeholder="Descripción del trabajo"
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ verticalAlign: 'top' }}>
                                {isCatalog
                                  ? (newInstallation.installation_service?.unidad || '')
                                  : (newInstallation.unidad || '')}
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  sx={{ width: 130 }}
                                  /* Phase 8.7 Round A, item 5: `||` en vez de `??` -
                                     evita mostrar un "0" prellenado en modo
                                     personalizada si mano_obra_m2 llega en 0 (p.ej.
                                     al editar una fila que se guardó en $0), en vez
                                     de solo cuando es null/undefined. */
                                  value={newInstallation.mano_obra_m2 || (isCatalog ? calculateInstallationPrice() : '')}
                                  onChange={(e) => setNewInstallation(prev => ({
                                    ...prev,
                                    mano_obra_m2: parseFloat(e.target.value) || 0
                                  }))}
                                  InputProps={{
                                    endAdornment: <span style={{ marginLeft: '4px', color: '#666', fontSize: '0.75rem' }}>{rowCurrency}</span>
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  required
                                  sx={{ width: 90 }}
                                  value={newInstallation.cantidad_manual ?? ''}
                                  onChange={(e) => setNewInstallation(prev => ({
                                    ...prev,
                                    // B6: vacío = undefined (no cae a 1); bloquea el alta abajo
                                    cantidad_manual: e.target.value === '' ? undefined : (parseFloat(e.target.value) || undefined)
                                  }))}
                                  inputProps={{ min: 0.1, step: 0.1 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                {isConsultarRow ? 'Consultar' : formatInstallationCurrency(rowTotal, rowCurrency)}
                              </TableCell>
                              <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={editingInstallationIndex ? handleUpdateInstallation : handleAddInstallation}
                                  disabled={disableSave}
                                  title={editingInstallationIndex ? 'Guardar Cambios' : 'Agregar'}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setInstallationDialog(false);
                                    setSelectedEnvironmentIndex(null);
                                    setInstallationMode('catalog');
                                    setNewInstallation({});
                                    setEditingInstallationIndex(null);
                                  }}
                                  title="Cancelar"
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })()}

                        {/* Footer unificado (walkthrough PO 2026-09-11): mismo
                            formato que Materiales. Diferencias de MO: sin
                            ambientes, sin descuento por producto, y SIN "+ IVA"
                            (MO no lleva IVA - CLAUDE §6/§8.9). Orden: Subtotal
                            (bruto) -> Descuento (%) input -> Descuento (monto,
                            oculto si 0) -> Total (neto) fondo negro/blanco. No se
                            cambia ningún cálculo de moneda ni de descuento. */}
                        {/* 1. Subtotal Instalación (bruto, antes del descuento) */}
                        <TableRow>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                            Subtotal Instalación:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                            {(() => {
                              const totales = { ARS: 0, USD: 0, other: 0 };

                              formData.environments.forEach(env => {
                                if (!env.installations || env.installations.length === 0) return;

                                // Sumar todas las instalaciones del ambiente
                                // - moneda por fila restaurada (CLAUDE.md
                                // §8.5, 2026-09-01): catálogo usa
                                // installation_service.currency, 'custom'
                                // sigue siendo siempre USD.
                                env.installations.forEach(installation => {
                                  let subtotal = 0;
                                  let currency = 'USD';

                                  if (installation.installation_service) {
                                    const precio = installation.mano_obra_m2 || installation.installation_service.precio;
                                    if (precio) {
                                      subtotal = precio * (installation.cantidad_manual ?? 1);
                                    }
                                    currency = installation.installation_service.currency || 'USD';
                                  } else if (installation.mano_obra_m2) {
                                    // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                    const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                    subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
                                  }

                                  if (currency === 'USD') {
                                    totales.USD += subtotal;
                                  } else if (currency === 'ARS') {
                                    totales.ARS += subtotal;
                                  } else {
                                    totales.other += subtotal;
                                  }
                                });
                              });

                              const breakdown = formatInstalacionBreakdown(totales.USD, totales.ARS);
                              return totales.other > 0 ? `${breakdown} + ${totales.other.toFixed(2)}` : breakdown;
                            })()}
                          </TableCell>
                        </TableRow>

                        {/* 2. Input de descuento de sección */}
                        <TableRow>
                          <TableCell colSpan={7} align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                              <Typography sx={{ fontWeight: 600 }}>Descuento Instalación (%)</Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={formData.descuento_instalacion ? Math.round(formData.descuento_instalacion) : ''}
                                onChange={handleChange('descuento_instalacion')}
                                inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* 3. Descuento (monto = bruto - neto = descuento de sección) - oculto si 0 */}
                        {formData.descuento_instalacion > 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                              Descuento:
                            </TableCell>
                            <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                              {`- ${formatInstalacionBreakdown(
                                totals.instalacionUSD * (formData.descuento_instalacion / 100),
                                totals.instalacionARS * (formData.descuento_instalacion / 100)
                              )}`}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* 4. Total Instalación (neto) - fondo negro/blanco, SIN "+ IVA" */}
                        <TableRow sx={{ backgroundColor: 'common.black' }}>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                            Total Instalación:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                            {formatInstalacionBreakdown(totals.instalacionUSDConDescuento, totals.instalacionARSConDescuento)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Alert severity="info">
                  No hay servicios de instalación configurados. Agregue instalación a los ambientes según sea necesario.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* 4. Tarjeta de Fletes */}
          <Card>
            <CardHeader 
              title="Fletes y Envíos"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<LocalShipping color="primary" />}
              subheader={`${formData.freights.length} flete(s) configurado(s)`}
            />
            <CardContent>
              {(
                <Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* "+" inline en vez del botón "Agregar Flete" del
                            header (Phase 8.7 Round A, item 3) - ocupa el
                            lugar de la primera fila de ítem de esta tabla. */}
                        {!freightDialog && (
                          <TableRow>
                            <TableCell colSpan={3} align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                title="Agregar Flete"
                                onClick={() => setFreightDialog(true)}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                        {formData.freights.map((freight, index) => (
                          <TableRow key={`freight-${index}`}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {freight.freight_rate?.descripcion || freight.tipo_envio}
                              </Typography>
                              {freight.codigo && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Código: {freight.codigo}
                                </Typography>
                              )}
                              <TextField
                                size="small"
                                value={freight.descripcion_detalle || ''}
                                onChange={(e) => {
                                  const newFreights = [...formData.freights];
                                  newFreights[index] = { ...newFreights[index], descripcion_detalle: e.target.value };
                                  setFormData(prev => ({ ...prev, freights: newFreights }));
                                }}
                                placeholder="Nota (opcional)"
                                inputProps={{ maxLength: 150 }}
                                sx={{ minWidth: 200, mt: 0.5 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(freight.monto, 'flete')}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleEditFreight(index)}
                                color="primary"
                                title="Editar"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteFreight(index)}
                                color="error"
                                title="Eliminar"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Fila inline para agregar un flete - reemplaza el
                            Dialog "Flete" que existía antes (conversión a
                            fila editable, 2026-09-05). Preserva el toggle
                            Catálogo/Manual, el picker de FreightRate y el
                            threading de freight_rate_id/codigo. */}
                        {freightDialog && (
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell sx={{ minWidth: 280, verticalAlign: 'top' }}>
                              <Stack spacing={1}>
                                <ToggleButtonGroup
                                  value={freightMode}
                                  exclusive
                                  size="small"
                                  onChange={(e, newMode) => {
                                    if (newMode !== null) {
                                      setFreightMode(newMode);
                                      setNewFreight({
                                        tipo_envio: '',
                                        monto: 0,
                                        tipo: 'fijo',
                                        currency: 'ARS'
                                      });
                                    }
                                  }}
                                >
                                  <ToggleButton value="catalog">Catálogo</ToggleButton>
                                  <ToggleButton value="manual">Manual</ToggleButton>
                                </ToggleButtonGroup>
                                {freightMode === 'catalog' ? (
                                  <>
                                    <Autocomplete
                                      key={fletePickerTick}
                                      openOnFocus
                                      size="small"
                                      options={freightRates}
                                      getOptionLabel={(option: FreightRate) =>
                                        `${option.zone}${option.descripcion ? ' - ' + option.descripcion : ''}${option.codigo ? ' (' + option.codigo + ')' : ''}`
                                      }
                                      value={newFreight.freight_rate || null}
                                      onChange={(event, newValue) => handleFreightRateSelect(newValue)}
                                      loading={loadingFreightRates}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          autoFocus
                                          label="Zona / Tarifa *"
                                          placeholder="Buscar por zona, código o descripción..."
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                {loadingFreightRates ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                      renderOption={(props: any, option: FreightRate) => (
                                        <Box component="li" {...props}>
                                          <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                              {option.zone}{option.descripcion ? ` - ${option.descripcion}` : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Hasta {option.peso_max} kg / {option.m2_max} m² | Precio: {formatInstallationCurrency(option.price, option.currency || 'ARS')}
                                              {option.codigo ? ` | Código: ${option.codigo}` : ''}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      )}
                                      filterOptions={(options: FreightRate[], { inputValue }: { inputValue: string }) => {
                                        if (!inputValue) return options;
                                        const lowerInput = inputValue.toLowerCase();
                                        return options.filter((option: FreightRate) =>
                                          option.zone.toLowerCase().includes(lowerInput) ||
                                          (option.codigo && option.codigo.toLowerCase().includes(lowerInput)) ||
                                          (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput))
                                        );
                                      }}
                                    />
                                    {newFreight.freight_rate && (
                                      <Typography variant="caption" color="text.secondary">
                                        Capacidad: hasta {newFreight.freight_rate.peso_max} kg / {newFreight.freight_rate.m2_max} m²
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <TextField
                                    size="small"
                                    label="Tipo de flete *"
                                    value={newFreight.tipo_envio}
                                    onChange={(e) => setNewFreight(prev => ({
                                      ...prev,
                                      tipo_envio: e.target.value
                                    }))}
                                    placeholder="Ej: Flete y acarreo especial"
                                  />
                                )}
                                <TextField
                                  size="small"
                                  label="Nota (opcional)"
                                  value={newFreight.descripcion_detalle || ''}
                                  onChange={(e) => setNewFreight(prev => ({
                                    ...prev,
                                    descripcion_detalle: e.target.value
                                  }))}
                                  placeholder="Detalles del flete"
                                  inputProps={{ maxLength: 150 }}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                              <TextField
                                size="small"
                                type="number"
                                sx={{ width: 130 }}
                                value={newFreight.monto || ''}
                                onChange={(e) => setNewFreight(prev => ({
                                  ...prev,
                                  monto: parseFloat(e.target.value) || 0
                                }))}
                                InputProps={{
                                  endAdornment: <span style={{ marginLeft: '4px', color: '#666', fontSize: '0.75rem' }}>{newFreight.currency === 'USD' ? 'U$' : 'AR$'}</span>
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={editingFreightIndex !== null ? handleUpdateFreight : handleAddFreight}
                                disabled={freightMode === 'catalog' ? !newFreight.freight_rate_id : (!newFreight.tipo_envio.trim() || !newFreight.monto || newFreight.monto <= 0)}
                                title={editingFreightIndex !== null ? 'Guardar Cambios' : 'Agregar'}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setFreightDialog(false);
                                  setFreightMode('catalog');
                                  setNewFreight({
                                    tipo_envio: '',
                                    monto: 0,
                                    tipo: 'fijo',
                                    currency: 'ARS'
                                  });
                                  setEditingFreightIndex(null);
                                }}
                                title="Cancelar"
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Footer unificado (walkthrough PO 2026-09-11): mismo
                            formato que Materiales. Diferencias de Flete: sin
                            ambientes, sin descuento por producto, y SIN "+ IVA"
                            (Flete no lleva IVA - CLAUDE §6/§8.9). Orden: Subtotal
                            (bruto) -> Descuento (%) input -> Descuento (monto,
                            oculto si 0) -> Total (neto) fondo negro/blanco. No se
                            cambia el passthrough de moneda ni el cálculo. */}
                        {/* 1. Subtotal Fletes (bruto, antes del descuento) */}
                        <TableRow>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Subtotal Fletes:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(totals.fletes, 'flete')}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>

                        {/* 2. Input de descuento de sección */}
                        <TableRow>
                          <TableCell colSpan={2} align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                              <Typography sx={{ fontWeight: 600 }}>Descuento Fletes (%)</Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={formData.descuento_fletes ? Math.round(formData.descuento_fletes) : ''}
                                onChange={handleChange('descuento_fletes')}
                                inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>

                        {/* 3. Descuento (monto = bruto - neto = descuento de
                            sección) - oculto si 0. Restaurado (walkthrough PO
                            2026-09-12 r3): simétrico con Mano de Obra, revierte
                            la remoción Flete-only del r2. */}
                        {formData.descuento_fletes > 0 && (
                          <TableRow>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Descuento:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {`- ${formatCurrency(totals.fletes * (formData.descuento_fletes / 100), 'flete')}`}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        )}

                        {/* Total Fletes (neto) - fondo negro/blanco, SIN "+ IVA" */}
                        <TableRow sx={{ backgroundColor: 'common.black' }}>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                            Total Fletes:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                            {formatCurrency(totals.fletesConDescuento, 'flete')}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Condiciones Adicionales */}
          <Card>
            <CardHeader 
              title="Condiciones Adicionales"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
              <TextField
                fullWidth
                label="Condiciones Adicionales"
                multiline
                rows={8}
                value={formData.condiciones_adicionales}
                onChange={handleChange('condiciones_adicionales')}
                placeholder="Observaciones adicionales"
              />
              
              <Box mt={2} display="flex" justifyContent="center">
                <Button 
                  variant="outlined" 
                  onClick={() => setShowCondiciones(!showCondiciones)}
                  startIcon={showCondiciones ? <ExpandMore /> : <Receipt />}
                >
                  {showCondiciones ? 'Ocultar Condiciones Obligatorias de Contratación' : 'Ver Condiciones Obligatorias de Contratación'}
                </Button>
              </Box>
              
              {showCondiciones && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Condiciones Obligatorias de Contratación:</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                      {formData.condiciones_contratacion}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* 5. Tarjeta de Resumen Financiero */}
          <Card>
            <CardHeader 
              title="Resumen Financiero"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={<Receipt color="primary" />}
            />
            <CardContent>
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {ivaPercentage > 0 ? `Total Materiales (incluye ${ivaPercentage}% IVA)` : 'Total Materiales'}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(totals.materialesConIva, 'material')}
                    </Typography>
                    {totals.materialesConDescuento < totals.materialesUSD && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin descuento: {formatCurrency(totals.materialesUSDConIva, 'material')}
                      </Typography>
                    )}
                    {ivaPercentage > 0 && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin IVA: {formatCurrency(totals.materialesConDescuento, 'material')}
                      </Typography>
                    )}
                  </Box>
                </Box>



                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Mano de Obra
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formData.descuento_instalacion > 0
                        ? formatInstalacionBreakdown(totals.instalacionUSDConDescuento, totals.instalacionARSConDescuento)
                        : formatInstalacionBreakdown(totals.instalacionUSD, totals.instalacionARS)}
                    </Typography>
                    {formData.descuento_instalacion > 0 && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin descuento: {formatInstalacionBreakdown(totals.instalacionUSD, totals.instalacionARS)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Flete
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formData.descuento_fletes > 0 ? formatCurrency(totals.fletesConDescuento, 'flete') : formatCurrency(totals.fletes, 'flete')}
                    </Typography>
                    {formData.descuento_fletes > 0 && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Sin descuento: {formatCurrency(totals.fletes, 'flete')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="primary.main" borderRadius={1}>
                    <Typography variant="subtitle2" color="white" gutterBottom>
                      Total USD (con descuento)
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {formatCurrency(totals.materialesConDescuento, 'material')}
                    </Typography>
                  </Box>
                </Box>

                <Box flex="1 1 200px">
                  <Box textAlign="center" p={2} bgcolor="success.main" borderRadius={1}>
                    <Typography variant="subtitle2" color="white" gutterBottom>
                      Total ARS (con descuento)
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {/* Solo la porción de Mano de Obra realmente en ARS
                          (CLAUDE.md §8.5, restaurado 2026-09-01) - la
                          porción en USD no pertenece a un total etiquetado
                          ARS. Fletes acá se asume siempre ARS, sin cambios
                          en este formulario de creación. */}
                      {formatCurrency(totals.instalacionARSConDescuento + totals.fletesConDescuento, 'instalacion')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Item D: diálogo de validación/errores al crear - prominente, no
              una alerta silenciosa al pie de la página. */}
          <Dialog
            open={submitErrorDialog.open}
            onClose={() => setSubmitErrorDialog({ open: false, messages: [] })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ color: 'error.main' }}>
              No se pudo crear el presupuesto
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Revisá lo siguiente antes de volver a intentar:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {submitErrorDialog.messages.map((msg, i) => (
                  <li key={i}>
                    <Typography variant="body2">{msg}</Typography>
                  </li>
                ))}
              </ul>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => setSubmitErrorDialog({ open: false, messages: [] })}
              >
                Entendido
              </Button>
            </DialogActions>
          </Dialog>

        </Stack>
      </form>

      {/* Diálogos */}
      
      {/* Dialog para nuevo ambiente */}
      <Dialog
        open={environmentDialog}
        onClose={() => setEnvironmentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuevo Ambiente</DialogTitle>
        <DialogContent
          onKeyDown={(e) => {
            // Phase 8.7 Round A, item 6: Enter dispara la acción por defecto -
            // salvo en el textarea de Descripción, donde Enter debe seguir
            // insertando un salto de línea.
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA' && newEnvironment.nombre.trim()) {
              e.preventDefault();
              handleAddEnvironment();
            }
          }}
        >
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre del Ambiente"
              value={newEnvironment.nombre}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                nombre: e.target.value
              }))}
              placeholder="ej: Sala de estar, Cocina, Dormitorio principal"
              required
            />
            
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={newEnvironment.descripcion}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                descripcion: e.target.value
              }))}
              placeholder="Descripción detallada del ambiente"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvironmentDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddEnvironment}
            variant="contained"
            disabled={!newEnvironment.nombre.trim()}
          >
            Agregar Ambiente
          </Button>
        </DialogActions>
      </Dialog>


      {/* Alerta de stock */}
      {showAlertStock && alertStock && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setShowAlertStock(false);
                setAlertStock(null);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {alertStock}
        </Alert>
      )}

      <Snackbar
        open={showAlertStock && !!alertStock}
        autoHideDuration={null}
        onClose={() => {
          setShowAlertStock(false);
          setAlertStock(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: '400px',
            justifyContent: 'center',
            alignItems: 'center',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'fixed',
            zIndex: 1500,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            boxShadow: 8,
          }
        }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setShowAlertStock(false);
                setAlertStock(null);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: '100%',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            bgcolor: '#d32f2f', // Rojo fuerte
            color: '#fff',
            border: '2px solid #b71c1c',
            boxShadow: 8,
          }}
        >
          {alertStock}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetFormNew;
